import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import Sidebar from "./components/Sidebar.tsx";
import BottomNav from "./components/BottomNav.tsx";
import Header from "./components/Header.tsx";
import Login from "./components/Login.tsx";
import DashboardView from "./views/DashboardView.tsx";
import StudentsView from "./views/StudentsView.tsx";
import MaterialGeneratorView from "./views/MaterialGeneratorView.tsx";
import AttendanceView from "./views/AttendanceView.tsx";
import AgendaView from "./views/AgendaView.tsx";
import AdminAgendaView from "./views/AdminAgendaView.tsx";
import ScheduleView from "./views/ScheduleView.tsx";
import GradingView from "./views/GradingView.tsx";
import NilaiRaporView from "./views/NilaiRaporView.tsx";
import TeachersManagementView from "./views/TeachersManagementView.tsx";
import ClassesManagementView from "./views/ClassesManagementView.tsx";
import LearningObjectivesView from "./views/LearningObjectivesView.tsx";
import SettingsView from "./views/SettingsView.tsx";
import ProfileView from "./views/ProfileView.tsx";
import StudentProfileView from "./views/StudentProfileView.tsx";
import StudentAgendaView from "./views/StudentAgendaView.tsx";
import SubjectSelectionView from "./views/SubjectSelectionView.tsx";
import TugasView from "./views/TugasView.tsx";
import ProfileAccessHistoryView from "./views/ProfileAccessHistoryView.tsx";
import PeriodSettingsView from "./views/PeriodSettingsView.tsx";
import MaintenanceView from "./views/MaintenanceView.tsx";
import WaliRombelView from "./views/WaliRombelView.tsx";
import PerwalianView from "./views/PerwalianView.tsx";
import DatabaseManager from "./components/DatabaseManager.tsx";
import {
  View,
  UserRole,
  Teacher,
  Student,
  AgendaEntry,
  ScheduleEntry,
  LearningObjective,
  AssessmentGrade,
  SasGrade,
  FormativeGradeTP,
  AttendanceRecord,
  AttendanceStatus,
  ActivityType,
  FormativeLevel,
  SchoolSettings,
  Tugas,
  TugasSubmission,
  TugasSession,
  ProfileAccessLog,
  TugasType,
  LearningContent,
  CourseModule,
  CourseChapter,
  CourseQuiz,
  CourseSubmission,
  AppNotification,
  HomeroomJournalNote,
  normalizeClassName,
  StudentAcademicProgress,
  StudentExtracurricular,
} from "./types.ts";
import { motion, AnimatePresence } from "motion/react";
import { GraduationCap, Loader2, AlertTriangle, X } from "lucide-react";
import {
  collection,
  getDocs,
  setDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  where,
  limit,
  orderBy,
  writeBatch,
} from "firebase/firestore";
import { db } from "./src/lib/firebase";
import {
  handleFirestoreError,
  OperationType,
} from "./src/lib/firestore-errors";
import { getNextThemeId } from "./src/themeConfig.ts";

const App: React.FC = () => {
  const [appTheme, setAppTheme] = useState<string>(() => {
    return localStorage.getItem("smpn1_app_theme") || "wzc-themes";
  });

  const handleNextTheme = useCallback(() => {
    setAppTheme((prev) => {
      const next = getNextThemeId(prev);
      localStorage.setItem("smpn1_app_theme", next);
      document.documentElement.setAttribute("data-app-theme", next);
      return next;
    });
  }, []);

  const handleSelectTheme = useCallback((themeId: string) => {
    setAppTheme(themeId);
    localStorage.setItem("smpn1_app_theme", themeId);
    document.documentElement.setAttribute("data-app-theme", themeId);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-app-theme", appTheme);
  }, [appTheme]);

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem("smpn1_isAuthenticated") === "true";
  });
  const [role, setRole] = useState<UserRole>(() => {
    return (
      (localStorage.getItem("smpn1_userRole") as UserRole) || UserRole.Guru
    );
  });
  const [activeUser, setActiveUser] = useState<any>(() => {
    const savedUser = localStorage.getItem("smpn1_activeUser");
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [activeView, setActiveView] = useState<View>(() => {
    const savedRole = localStorage.getItem("smpn1_userRole") as UserRole;
    return savedRole === UserRole.Siswa
      ? View.SubjectSelection
      : View.Dashboard;
  });
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [themeMode, setThemeMode] = useState<"light" | "dark" | "system">(
    () => {
      const saved = localStorage.getItem("smpn1_themeMode");
      if (saved === "light" || saved === "dark" || saved === "system") {
        return saved;
      }
      return "system";
    },
  );

  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (themeMode === "system") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    }
    return themeMode === "dark";
  });

  // Update isDarkMode whenever themeMode or system preference changes
  useEffect(() => {
    if (themeMode === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      setIsDarkMode(mediaQuery.matches);

      const handleChange = (e: MediaQueryListEvent) => {
        setIsDarkMode(e.matches);
      };

      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    } else {
      setIsDarkMode(themeMode === "dark");
    }
  }, [themeMode]);

  // Sync state to localStorage and document class
  useEffect(() => {
    localStorage.setItem("smpn1_themeMode", themeMode);
    localStorage.setItem("smpn1_isDarkMode", isDarkMode.toString());

    if (isDarkMode) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
  }, [themeMode, isDarkMode]);
  const [quotaError, setQuotaError] = useState<string | null>(null);

  const checkQuotaError = useCallback((error: any) => {
    const errorStr = error instanceof Error ? error.message : String(error);
    if (
      errorStr.toLowerCase().includes("quota") ||
      errorStr.toLowerCase().includes("limit exceeded")
    ) {
      const msg =
        "Batas baca gratis (Quota) dari Firebase Firestore per hari ini telah habis. Data tidak dapat dimuat atau disimpan untuk sementara. Kuota akan direset otomatis esok hari. Jika perlu, naikkan plan limit di Firebase Console.";
      setQuotaError(msg);
      setIsLoading(false); // Stop the spinner to show maintenance or banner
      return true;
    }
    return false;
  }, []);

  const mainContentRef = useRef<HTMLElement>(null);

  // Reset quota error periodically to try recovering
  useEffect(() => {
    if (quotaError) {
      const timer = setTimeout(() => {
        handleManualRetry();
      }, 300000); // Try again every 5 minutes
      return () => clearTimeout(timer);
    }
  }, [quotaError]);

  const handleManualRetry = useCallback(() => {
    setIsLoading(true);
    setLoadingStatus("Mengecek kuota Firestore...");
    setQuotaError(null);
    isFetchingRef.current = false;
  }, []);
  const [targetGradingAgenda, setTargetGradingAgenda] =
    useState<AgendaEntry | null>(null);
  const [targetTugasId, setTargetTugasId] = useState<string | null>(null);
  const [targetAgendaTab, setTargetAgendaTab] = useState<"input" | "recap">(
    "input",
  );
  const [targetAttendanceTab, setTargetAttendanceTab] = useState<
    "today" | "pending" | "recap"
  >("today");
  const [selectedStudentForProfile, setSelectedStudentForProfile] =
    useState<Student | null>(null);
  const [selectedStudentForGrading, setSelectedStudentForGrading] =
    useState<Student | null>(null);
  const [selectedSubjectForStudent, setSelectedSubjectForStudent] =
    useState<string>("");

  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTo(0, 0);
    }
  }, [activeView]);

  const getStoredData = (key: string, defaultValue: any) => {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : defaultValue;
  };

  const initialClasses = [
    "7A",
    "7B",
    "7C",
    "8A",
    "8B",
    "8C",
    "9A",
    "9B",
    "9C",
    "9D",
    "9E",
    "9F",
    "9G",
    "9H",
    "9I",
    "9J",
    "9K",
  ];

  const [classes, setClasses] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem("smpn1_classes");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const cleaned = Array.from(new Set<string>(parsed.map(c => normalizeClassName(c)).filter(Boolean)))
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
          return cleaned;
        }
      }
    } catch (e) {}
    return initialClasses;
  });

  const [schoolSettings, setSchoolSettings] = useState<SchoolSettings>(() => {
    try {
      const saved = localStorage.getItem("smpn1_settings");
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {}
    return {
      schoolName: "SMP NEGERI 1 KAWALI",
      principalName: "Nanang Budiawan, S.Pd, M.Pd",
      principalNip: "197501012000031001",
      address: "Jl. Raya Kawali No. 1, Kawali, Ciamis, Jawa Barat 46253",
      logoUrl:
        "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9c/Logo_Kemdikbud.png/800px-Logo_Kemdikbud.png",
      departmentLogoUrl:
        "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Logo_Kabupaten_Ciamis.png/800px-Logo_Kabupaten_Ciamis.png",
      academicCalendarUrl: "",
      announcements: [],
      showGradesToStudents: true,
      isStudentMaintenanceMode: false,
      periodTimes: Array.from({ length: 10 }, (_, i) => ({
        period: i + 1,
        startTime: "07:00",
        endTime: "07:40",
      })),
      dayPeriodTimes: {},
    };
  });

  const handleUpdateSettings = async (newSettings: SchoolSettings) => {
    const stableId = (schoolSettings as any)?.id || (newSettings as any)?.id || "default_settings";
    const settingsToSave = { ...newSettings, id: stableId };
    setSchoolSettings(settingsToSave);
    try {
      localStorage.setItem("smpn1_settings", JSON.stringify(settingsToSave));
    } catch (e) {}
    return await saveToApi("settings", settingsToSave, "POST", stableId);
  };

  // API Base URL - Updated for Vercel backend
  const API_BASE_URL = "/api";

  const [isLoading, setIsLoading] = useState(true);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState(
    "Menghubungkan ke server...",
  );
  const [lastSyncTime, setLastSyncTime] = useState<Record<string, number>>(
    () => {
      const saved = localStorage.getItem("smpn1_lastSyncTime");
      return saved ? JSON.parse(saved) : {};
    },
  );

  const saveSyncTime = (collectionName: string) => {
    setLastSyncTime((prev) => {
      const updated = { ...prev, [collectionName]: Date.now() };
      localStorage.setItem("smpn1_lastSyncTime", JSON.stringify(updated));
      return updated;
    });
  };

  const saveToApi = async (
    collectionName: string,
    data: any,
    method: "POST" | "PUT" | "DELETE" = "POST",
    id?: string,
  ): Promise<any> => {
    if (!db) return { error: true, message: "Database not ready" };

    // Normalize collection name for Firebase
    const pathMap: Record<string, string> = {
      settings: "schoolSettings",
      attendance: "attendanceRecords",
      homeroomAttendance: "homeroomAttendanceRecords",
      "assessment-grades": "assessmentGrades",
      "sas-grades": "sasGrades",
      "formative-grades": "formativeGrades",
      objectives: "learningObjectives",
      "learning-content": "learningContents",
      "tugas-submissions": "tugasSubmissions",
      "profile-access-logs": "profileAccessLogs",
      homeroomNotes: "homeroomNotes",
      extracurriculars: "studentExtracurriculars",
    };

    const targetCollection = pathMap[collectionName] || collectionName;
    const docId =
      id || data?.id || (method === "POST" ? crypto.randomUUID() : null);

    if (!docId && method !== "POST") {
      console.warn(
        `No ID provided for ${method} operation on ${targetCollection}`,
      );
      return { error: true, message: "Missing document ID" };
    }

    try {
      const finalId = docId || crypto.randomUUID();
      const docRef = doc(db, targetCollection, finalId);

      if (method === "DELETE") {
        await deleteDoc(docRef);
        console.log(`Deleted from Firebase: ${targetCollection}/${finalId}`);
        return { success: true };
      } else {
        const payload = {
          ...data,
          id: finalId,
          updatedAt: new Date().toISOString(),
        };
        // Remove undefined fields which Firestore doesn't like
        Object.keys(payload).forEach(
          (key) => payload[key] === undefined && delete payload[key],
        );

        await setDoc(docRef, payload, { merge: true });
        console.log(`Saved to Firebase: ${targetCollection}/${finalId}`);
        return payload;
      }
    } catch (error: any) {
      console.error(`Firebase Error saving to ${targetCollection}:`, error);
      checkQuotaError(error);
      return { error: true, message: String(error) };
    }
  };

  const schoolSettingsRef = useRef(schoolSettings);
  useEffect(() => {
    schoolSettingsRef.current = schoolSettings;
  }, [schoolSettings]);

  useEffect(() => {
    localStorage.setItem("smpn1_classes", JSON.stringify(classes));
  }, [classes]);

  const [teachers, setTeachers] = useState<Teacher[]>(() =>
    getStoredData("smpn1_teachers", []),
  );
  const [learningObjectives, setLearningObjectives] = useState<
    LearningObjective[]
  >(() => getStoredData("smpn1_objectives", []));
  const [students, setStudents] = useState<Student[]>(() =>
    getStoredData("smpn1_students", []),
  );
  const [agendas, setAgendas] = useState<AgendaEntry[]>(() =>
    getStoredData("smpn1_agendas", []),
  );
  const [attendanceRecords, setAttendanceRecords] = useState<
    AttendanceRecord[]
  >(() => getStoredData("smpn1_attendance", []));
  const [homeroomAttendance, setHomeroomAttendance] = useState<
    AttendanceRecord[]
  >(() => getStoredData("smpn1_homeroom_attendance", []));
  const [assessmentGrades, setAssessmentGrades] = useState<AssessmentGrade[]>(
    () => getStoredData("smpn1_assessmentGrades", []),
  );
  const [sasGrades, setSasGrades] = useState<SasGrade[]>(() =>
    getStoredData("smpn1_sasGrades", []),
  );
  const [formativeGradesTP, setFormativeGradesTP] = useState<
    FormativeGradeTP[]
  >(() => getStoredData("smpn1_formativeGrades", []));
  const [schedules, setSchedules] = useState<ScheduleEntry[]>(() =>
    getStoredData("smpn1_schedules", []),
  );
  const [tugasList, setTugasList] = useState<any[]>(() =>
    getStoredData("smpn1_tugasList", []),
  );
  const [tugasSubmissions, setTugasSubmissions] = useState<any[]>(() =>
    getStoredData("smpn1_tugasSubmissions", []),
  );
  const [tugasSessions, setTugasSessions] = useState<TugasSession[]>(() =>
    getStoredData("smpn1_tugasSessions", []),
  );
  const [learningContents, setLearningContents] = useState<LearningContent[]>(
    () => getStoredData("smpn1_learningContents", []),
  );
  const [profileAccessLogs, setProfileAccessLogs] = useState<any[]>(() =>
    getStoredData("smpn1_profileAccessLogs", []),
  );
  const [courseModules, setCourseModules] = useState<CourseModule[]>(() =>
    getStoredData("smpn1_courseModules", []),
  );
  const [courseSubmissions, setCourseSubmissions] = useState<
    CourseSubmission[]
  >(() => getStoredData("smpn1_courseSubmissions", []));
  const [notifications, setNotifications] = useState<AppNotification[]>(() =>
    getStoredData("smpn1_notifications", []),
  );
  const [waliRombel, setWaliRombel] = useState<any[]>(() =>
    getStoredData("smpn1_waliRombel", []),
  );
  const [homeroomNotes, setHomeroomNotes] = useState<HomeroomJournalNote[]>(() =>
    getStoredData("smpn1_homeroomNotes", []),
  );
  const [studentAcademicProgress, setStudentAcademicProgress] = useState<StudentAcademicProgress[]>(() =>
    getStoredData("smpn1_studentAcademicProgress", []),
  );
  const [extracurriculars, setExtracurriculars] = useState<StudentExtracurricular[]>(() =>
    getStoredData("smpn1_extracurriculars", []),
  );

  useEffect(() => {
    localStorage.setItem("smpn1_studentAcademicProgress", JSON.stringify(studentAcademicProgress));
  }, [studentAcademicProgress]);

  useEffect(() => {
    localStorage.setItem("smpn1_extracurriculars", JSON.stringify(extracurriculars));
  }, [extracurriculars]);

  const handleSaveExtracurricular = async (
    item: StudentExtracurricular,
    method: "POST" | "PUT" | "DELETE" = "POST",
  ) => {
    let updated: StudentExtracurricular[] = [];
    if (method === "DELETE") {
      updated = extracurriculars.filter((e) => e.id !== item.id);
    } else if (method === "PUT") {
      updated = extracurriculars.map((e) => (e.id === item.id ? item : e));
    } else {
      updated = [item, ...extracurriculars];
    }
    setExtracurriculars(updated);
    localStorage.setItem("smpn1_extracurriculars", JSON.stringify(updated));
    return await saveToApi("extracurriculars", item, method, item.id);
  };

  const handleSaveAcademicProgress = async (
    progress: StudentAcademicProgress,
    method: "POST" | "PUT" | "DELETE" = "POST",
  ) => {
    let updated: StudentAcademicProgress[] = [];
    if (method === "DELETE") {
      updated = studentAcademicProgress.filter((p) => p.id !== progress.id);
    } else if (method === "PUT") {
      updated = studentAcademicProgress.map((p) => (p.id === progress.id ? progress : p));
    } else {
      updated = [progress, ...studentAcademicProgress];
    }
    setStudentAcademicProgress(updated);
    localStorage.setItem("smpn1_studentAcademicProgress", JSON.stringify(updated));
    return await saveToApi("studentAcademicProgress", progress, method, progress.id);
  };

  const handleSaveHomeroomNote = async (
    note: HomeroomJournalNote,
    method: "POST" | "PUT" | "DELETE" = "POST",
  ) => {
    let updated: HomeroomJournalNote[] = [];
    if (method === "DELETE") {
      updated = homeroomNotes.filter((n) => n.id !== note.id);
    } else if (method === "PUT") {
      updated = homeroomNotes.map((n) => (n.id === note.id ? note : n));
    } else {
      updated = [note, ...homeroomNotes];
    }
    setHomeroomNotes(updated);
    localStorage.setItem("smpn1_homeroomNotes", JSON.stringify(updated));
    return await saveToApi("homeroomNotes", note, method, note.id);
  };

  // Cleanup logic to remove PHP Dasar course if it exists
  const hasCleanedUpRef = useRef(false);
  useEffect(() => {
    if (!isLoading && role === UserRole.Guru && !hasCleanedUpRef.current) {
      const phpModule = courseModules.find(
        (m) => m.title === "PHP Dasar" || m.id === "php-dasar-01",
      );
      if (phpModule) {
        hasCleanedUpRef.current = true;
        saveToApi("courseModules", null, "DELETE", phpModule.id);
      }
    }
  }, [isLoading, courseModules, role]);

  const isFetchingRef = useRef(false);
  const unsubscribesRef = useRef<Array<() => void>>([]);
  const fetchedOnDemandKeysRef = useRef<Set<string>>(new Set());

  // Fetch all data on mount and set up real-time sync with Firebase
  useEffect(() => {
    if (quotaError) return;
    if (!db) return;

    const endpointConfigs = [
      { name: "Guru & Staf", target: "teachers", setter: setTeachers },
      { name: "Data Siswa", target: "students", setter: setStudents },
      { name: "Agenda Kelas", target: "agendas", setter: setAgendas },
      {
        name: "Presensi Siswa",
        target: "attendanceRecords",
        setter: setAttendanceRecords,
      },
      {
        name: "Presensi Wali Kelas",
        target: "homeroomAttendanceRecords",
        setter: setHomeroomAttendance,
      },
      {
        name: "Nilai Sumatif",
        target: "assessmentGrades",
        setter: setAssessmentGrades,
      },
      { name: "Nilai SAS", target: "sasGrades", setter: setSasGrades },
      {
        name: "Nilai Formatif",
        target: "formativeGrades",
        setter: setFormativeGradesTP,
      },
      { name: "Daftar Tugas", target: "tugas", setter: setTugasList },
      {
        name: "Pengumpulan Tugas",
        target: "tugasSubmissions",
        setter: setTugasSubmissions,
      },
      {
        name: "Sesi Tugas Realtime",
        target: "tugasSessions",
        setter: setTugasSessions,
      },
      {
        name: "Materi Pembelajaran",
        target: "learningContents",
        setter: setLearningContents,
      },
      {
        name: "Log Akses",
        target: "profileAccessLogs",
        setter: setProfileAccessLogs,
      },
      {
        name: "Tujuan Pembelajaran",
        target: "learningObjectives",
        setter: (data: any[]) => {
          setLearningObjectives(data || []);
          try {
            localStorage.setItem("smpn1_objectives", JSON.stringify(data || []));
          } catch (e) {}
        },
      },
      { name: "Jadwal Pelajaran", target: "schedules", setter: setSchedules },
      {
        name: "Modul Kursus",
        target: "courseModules",
        setter: setCourseModules,
      },
      {
        name: "Hasil Kursus",
        target: "courseSubmissions",
        setter: setCourseSubmissions,
      },
      { name: "Notifikasi", target: "notifications", setter: setNotifications },
      { name: "Wali Rombel", target: "waliRombel", setter: setWaliRombel },
      { name: "Catatan Wali Kelas", target: "homeroomNotes", setter: setHomeroomNotes },
      {
        name: "Perkembangan Akademik",
        target: "studentAcademicProgress",
        setter: setStudentAcademicProgress,
      },
      {
        name: "Ekstrakurikuler Siswa",
        target: "studentExtracurriculars",
        setter: setExtracurriculars,
      },
      {
        name: "Pengaturan Sekolah",
        target: "schoolSettings",
        setter: (data: any[]) => {
          if (!data || data.length === 0) return;
          const sorted = [...data].sort((a: any, b: any) => {
            if (a.id === "default_settings") return -1;
            if (b.id === "default_settings") return 1;
            const timeA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
            const timeB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
            return timeB - timeA;
          });
          const settings = sorted[0];
          if (settings && settings.schoolName) {
            setSchoolSettings(settings);
            try {
              localStorage.setItem("smpn1_settings", JSON.stringify(settings));
            } catch (e) {}
            if (settings.classes && Array.isArray(settings.classes)) {
              const cleaned = Array.from(new Set<string>(settings.classes.map((c: string) => normalizeClassName(c)).filter(Boolean)))
                .sort((a: string, b: string) => a.localeCompare(b, undefined, { numeric: true }));
              setClasses(cleaned);
              localStorage.setItem(
                "smpn1_classes",
                JSON.stringify(cleaned),
              );
            }
          }
        },
      },
    ];

    const setupSnapshot = (config: any) => {
      let q = query(collection(db, config.target));
      
      // Added limit for performance-heavy collections
      if (config.target === 'profileAccessLogs') {
        q = query(collection(db, config.target), orderBy('timestamp', 'desc'), limit(10));
      }

      const unsub = onSnapshot(
        q,
        (snapshot) => {
          let data = snapshot.docs.map((doc) => {
            const d = doc.data();
            return { id: doc.id, ...d };
          });

          if (config.target === "teachers") {
            data = data.map((t: any) => {
              if (t.assignedClasses && Array.isArray(t.assignedClasses)) {
                const cleaned = Array.from(new Set<string>(t.assignedClasses.map((c: string) => normalizeClassName(c)).filter(Boolean)))
                  .sort((a: string, b: string) => a.localeCompare(b, undefined, { numeric: true }));
                return { ...t, assignedClasses: cleaned };
              }
              return t;
            });
          }

          config.setter(data);

          // Update activeUser if found
          if (
            activeUser &&
            (config.target === "teachers" || config.target === "students")
          ) {
            let updatedUser: any = data.find((u: any) => u.id === activeUser.id);
            if (updatedUser) {
              if (updatedUser.assignedClasses && Array.isArray(updatedUser.assignedClasses)) {
                const cleaned = Array.from(new Set<string>(updatedUser.assignedClasses.map((c: string) => normalizeClassName(c)).filter(Boolean)))
                  .sort((a: string, b: string) => a.localeCompare(b, undefined, { numeric: true }));
                updatedUser = { ...updatedUser, assignedClasses: cleaned };
              }
              setActiveUser(updatedUser);
              localStorage.setItem(
                "smpn1_activeUser",
                JSON.stringify(updatedUser),
              );
            }
          }
        },
        (error) => {
          console.error(`Firebase error on ${config.target}:`, error);
          checkQuotaError(error);
        },
      );
      unsubscribesRef.current.push(unsub);
    };

    const initData = async () => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;

      setIsLoading(true);
      setLoadingProgress(10);
      setLoadingStatus("Menghubungkan ke Firebase Realtime...");

      try {
        const isSiswa =
          (localStorage.getItem("smpn1_userRole") as UserRole) ===
          UserRole.Siswa;

        // Clear previous unsubs
        unsubscribesRef.current.forEach((u) => u());
        unsubscribesRef.current = [];

        // For real-time updates, we just set up snapshots for most things
        // Critical first
        const critical = [
          "schoolSettings",
          "teachers",
          "students",
          "schedules",
        ];

        endpointConfigs.forEach((config) => {
          if (critical.includes(config.target)) {
            setupSnapshot(config);
          }
        });

        setLoadingProgress(50);
        setLoadingStatus("Sinkronisasi data sekolah...");

        // If authenticated, load rest
        if (isAuthenticated) {
          endpointConfigs.forEach((config) => {
            if (!critical.includes(config.target)) {
              // If student, limit some collections
              // Also skip loading learningContents for non-students initially (load on-demand)
              if (
                config.target === "learningContents" ||
                (isSiswa && ["agendas", "learningObjectives", "attendanceRecords"].includes(config.target))
              ) {
                // These are lazy-loaded on-demand to save quota
              } else {
                setupSnapshot(config);
              }
            }
          });
        }

        setTimeout(() => {
          setIsLoading(false);
          isFetchingRef.current = false;
        }, 1500);
      } catch (error) {
        console.error("Error setting up data fetch:", error);
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    };

    initData();

    return () => {
      unsubscribesRef.current.forEach((unsub) => unsub());
      unsubscribesRef.current = [];
    };
  }, [isAuthenticated, role, activeUser?.id, activeUser?.class, quotaError]);

  const refreshTugasSubmissions = async () => {
    // With real-time snapshots, manual refresh is often unnecessary but kept for emergency manual triggers.
    // However, if the API backend is not in sync with Firestore, this could overwrite state with empty data.
    // We'll add a safety check to only update if we actually get data.
    try {
      // Refresh submissions
      const subRes = await fetch(`${API_BASE_URL}/tugas-submissions`);
      if (subRes.ok) {
        const json = await subRes.json();
        if (Array.isArray(json) && json.length > 0) {
          setTugasSubmissions(json);
        }
      }

      // Also refresh tugas list
      const tugRes = await fetch(`${API_BASE_URL}/tugas`);
      if (tugRes.ok) {
        const json = await tugRes.json();
        if (Array.isArray(json) && json.length > 0) {
          setTugasList(json);
        }
      }
    } catch (error) {
      console.error("Error refreshing tugas data:", error);
    }
  };

  // Wrapper for state updates that also syncs to API
  const handleUpdateTeacher = (updated: Teacher) => {
    let cleanTeacher = updated;
    if (updated.assignedClasses && Array.isArray(updated.assignedClasses)) {
      const cleaned = Array.from(new Set<string>(updated.assignedClasses.map((c: string) => normalizeClassName(c)).filter(Boolean)))
        .sort((a: string, b: string) => a.localeCompare(b, undefined, { numeric: true }));
      cleanTeacher = { ...updated, assignedClasses: cleaned };
    }
    const newTeachers = teachers.map((t) =>
      t.id === cleanTeacher.id ? cleanTeacher : t,
    );
    setTeachers(newTeachers);
    saveToApi("teachers", cleanTeacher, "PUT", cleanTeacher.id);
    if (activeUser && activeUser.id === cleanTeacher.id) {
      setActiveUser(cleanTeacher);
      localStorage.setItem("smpn1_activeUser", JSON.stringify(cleanTeacher));
    }
  };

  const handleUpdateStudent = (updated: Student) => {
    const newStudents = students.map((s) =>
      s.id === updated.id ? updated : s,
    );
    setStudents(newStudents);
    saveToApi("students", updated, "PUT", updated.id);
    if (activeUser && activeUser.id === updated.id) {
      setActiveUser(updated);
      localStorage.setItem("smpn1_activeUser", JSON.stringify(updated));
    }
  };

  const handleDeleteNotificationData = async (notif: any) => {
    if (!notif) return;

    // Check if it's a DB notification
    const isDbNotif = notifications.some((n) => n.id === notif.id);
    if (isDbNotif) {
      setNotifications((prev) => prev.filter((n) => n.id !== notif.id));
      await saveToApi("notifications", null, "DELETE", notif.id);
      return;
    }

    // Extract real ID from virtual notification ID
    let realId = "";
    let collection = "";

    // REMOVED DELETION FOR AGENDAS AND SUBMISSIONS:
    // Teacher's dismissal should only affect UI/localStorage/dismissedNotifications,
    // not delete student's grades or lesson history.

    if (notif.id.startsWith("notif-some-junk-data-")) {
      // reserved for actual junk data deletion if needed
    }

    if (realId && collection) {
      console.log(
        `Deleting ${collection} record ${realId} due to notification dismissal`,
      );
      await saveToApi(collection, null, "DELETE", realId);
    }
  };

  const handleSaveWali = async (item: any, method: 'POST' | 'PUT' | 'DELETE') => {
    const res = await saveToApi("waliRombel", item, method, item.id);
    if (!res.error) {
      setWaliRombel((prev) => {
        if (method === "DELETE") {
          return prev.filter((w) => w.id !== item.id);
        } else if (method === "PUT") {
          return prev.map((w) => (w.id === item.id ? { ...w, ...item } : w));
        } else {
          if (prev.some((w) => w.id === item.id)) {
            return prev.map((w) => (w.id === item.id ? { ...w, ...item } : w));
          }
          return [...prev, item];
        }
      });
    }
    return res;
  };

  const handleUpdateStudents = async (newStudents: Student[]) => {
    const oldStudents = [...(students || [])];
    setStudents(newStudents || []);

    // Simple sync logic: find the difference
    if ((newStudents || []).length > oldStudents.length) {
      // Added
      const added = (newStudents || []).filter(
        (s) => !oldStudents.some((old) => old.id === s.id),
      );
      if (added.length > 0) {
        added.forEach((a) => saveToApi("students", a, "POST", a.id));
      }
    } else if ((newStudents || []).length < oldStudents.length) {
      // Deleted
      const deleted = oldStudents.filter(
        (old) => !(newStudents || []).some((s) => s.id === old.id),
      );
      deleted.forEach((d) => saveToApi("students", null, "DELETE", d.id));
    } else {
      // Potential update
      const updated = (newStudents || []).filter((s) => {
        const old = oldStudents.find((o) => o.id === s.id);
        return old && JSON.stringify(old) !== JSON.stringify(s);
      });
      updated.forEach((u) => saveToApi("students", u, "PUT", u.id));
    }
  };

  const handleUpdateAgendas = async (newAgendas: AgendaEntry[]) => {
    const oldAgendas = [...(agendas || [])];
    setAgendas(newAgendas || []);

    if ((newAgendas || []).length > oldAgendas.length) {
      // Added
      const added = (newAgendas || []).filter(
        (a) => !oldAgendas.some((old) => old.id === a.id),
      );
      added.forEach((a) => saveToApi("agendas", a, "POST"));
    } else if ((newAgendas || []).length < oldAgendas.length) {
      // Deleted
      const deleted = oldAgendas.filter(
        (old) => !(newAgendas || []).some((a) => a.id === old.id),
      );
      deleted.forEach((d) => saveToApi("agendas", null, "DELETE", d.id));
    } else {
      // Updated
      const updated = (newAgendas || []).filter((a) => {
        const old = oldAgendas.find((o) => o.id === a.id);
        return old && JSON.stringify(old) !== JSON.stringify(a);
      });
      updated.forEach((u) => saveToApi("agendas", u, "PUT", u.id));
    }
  };

  const handleUpdateAgenda = async (updated: AgendaEntry) => {
    setAgendas((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    saveToApi("agendas", updated, "PUT", updated.id);
  };

  const handleUpdateObjectives = async (newObjs: LearningObjective[]) => {
    const oldObjs = [...(learningObjectives || [])];
    setLearningObjectives(newObjs || []);
    try {
      localStorage.setItem("smpn1_objectives", JSON.stringify(newObjs || []));
    } catch (e) {}

    const added = (newObjs || []).filter((o) => !oldObjs.some((old) => old.id === o.id));
    const deleted = oldObjs.filter((old) => !(newObjs || []).some((o) => o.id === old.id));
    const updated = (newObjs || []).filter((o) => {
      const old = oldObjs.find((oldObj) => oldObj.id === o.id);
      return old && JSON.stringify(old) !== JSON.stringify(o);
    });

    if (added.length > 0) {
      added.forEach((a) => saveToApi("objectives", a, "POST", a.id));
    }
    if (deleted.length > 0) {
      deleted.forEach((d) => saveToApi("objectives", null, "DELETE", d.id));
    }
    if (updated.length > 0) {
      updated.forEach((u) => saveToApi("objectives", u, "PUT", u.id));
    }
  };

  const handleSaveTugas = useCallback(
    async (tugas: Tugas) => {
      console.log("App: handleSaveTugas", tugas.id, tugas.title);
      const isExisting = tugasList.some((q) => q.id === tugas.id);
      if (isExisting) {
        setTugasList((prev) =>
          prev.map((q) => (q.id === tugas.id ? tugas : q)),
        );
        saveToApi("tugas", tugas, "PUT", tugas.id);
      } else {
        setTugasList((prev) => [...prev, tugas]);
        saveToApi("tugas", tugas, "POST");
      }
    },
    [tugasList],
  );

  const handleDeleteTugas = useCallback(
    async (id: string) => {
      console.log("App: handleDeleteTugas", id);
      const originalTugas = [...tugasList];
      setTugasList((prev) => prev.filter((q) => q.id !== id));
      const result = await saveToApi("tugas", null, "DELETE", id);
      if (result && result.error) {
        setTugasList(originalTugas);
        console.error("Gagal menghapus tugas:", result);
      } else {
        // Delete associated assessment grade
        const gradeId = `tugas-${id}`;
        setAssessmentGrades((prev) => prev.filter((g) => g.id !== gradeId));
        saveToApi("assessment-grades", null, "DELETE", gradeId);
      }
    },
    [tugasList],
  );

  const handleSaveCourseModule = useCallback(async (module: CourseModule) => {
    setCourseModules((prev) => {
      const isExisting = prev.some((m) => m.id === module.id);
      if (isExisting) {
        return prev.map((m) => (m.id === module.id ? module : m));
      }
      return [...prev, module];
    });
    saveToApi("courseModules", module, "PUT", module.id);
  }, []);

  const handleDeleteCourseModule = useCallback(async (id: string) => {
    setCourseModules((prev) => prev.filter((m) => m.id !== id));
    saveToApi("courseModules", null, "DELETE", id);
  }, []);

  const handleSaveCourseSubmission = useCallback(
    async (submission: CourseSubmission) => {
      setCourseSubmissions((prev) => {
        const isExisting = prev.some((s) => s.id === submission.id);
        if (isExisting) {
          return prev.map((s) => (s.id === submission.id ? submission : s));
        }
        return [...prev, submission];
      });
      saveToApi("courseSubmissions", submission, "POST");
    },
    [],
  );

  const handleSaveAssessmentGrade = useCallback(
    async (grade: AssessmentGrade) => {
      setAssessmentGrades((prev) => {
        const isExisting = prev.some((g) => g.id === grade.id);
        if (isExisting) {
          return prev.map((g) => (g.id === grade.id ? grade : g));
        }
        return [...prev, grade];
      });
      saveToApi("assessment-grades", grade, "POST");
    },
    [],
  );

  const handleSubmitTugas = useCallback(
    async (submission: TugasSubmission) => {
      console.log("App: handleSubmitTugas CALLED", submission);
      setTugasSubmissions((prev) => {
        const filtered = prev.filter((s) => s.id !== submission.id);
        return [...filtered, submission];
      });
      saveToApi("tugas-submissions", submission, "POST");

      // Sync to AssessmentGrades
      const tugas = tugasList.find((q) => q.id === submission.tugasId);

      // Create Notification for Teacher
      if (tugas && activeUser && role === UserRole.Siswa) {
        const student = activeUser;
        const notifId = `notif-sub-${submission.id}`;
        console.log(
          "App: Creating notification for teacher",
          tugas.teacherId,
          "from student",
          student.name,
        );

        const notification: AppNotification = {
          id: notifId,
          userId: tugas.teacherId || "", // Recipient: Teacher
          title: "Tugas Selesai",
          message: `${student.name} dari kelas ${activeUser.class || ""} telah menyelesaikan tugas "${tugas.title}".`,
          type: "success",
          timestamp: new Date().toISOString(),
          relatedId: tugas.id,
          isRead: false,
          senderName: student.name,
        };

        const payload = {
          ...notification,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        };

        // Only update locally if we are the recipient (rare in this case but safe)
        if (tugas.teacherId === activeUser.id) {
          setNotifications((prev) => {
            const filtered = prev.filter((n) => n.id !== notifId);
            return [...filtered, payload];
          });
        }

        saveToApi("notifications", payload, "POST", notifId);
      }

      if (tugas) {
        const activityType = tugas.isSummative
          ? ActivityType.SUMATIF
          : ActivityType.KBM_DAN_QUIZ;
        const gradeId = `tugas-${tugas.id}`;

        let gradeToSave: AssessmentGrade | null = null;
        let method: "POST" | "PUT" = "POST";

        setAssessmentGrades((prev) => {
          const existing = prev.find((g) => g.id === gradeId);

          if (existing) {
            gradeToSave = {
              ...existing,
              scores: {
                ...existing.scores,
                [submission.studentId]: submission.score,
              },
            };
            method = "PUT";
            return prev.map((g) => (g.id === gradeId ? gradeToSave! : g));
          } else {
            gradeToSave = {
              id: gradeId,
              class: tugas.class,
              tpId: tugas.tpId || "",
              activityType,
              assessmentType: tugas.isSummative
                ? "Sumatif per TP"
                : "KBM dan quiz (jika ada)",
              date: new Date().toISOString().split("T")[0],
              scores: { [submission.studentId]: submission.score },
            };
            method = "POST";
            return [...prev, gradeToSave!];
          }
        });

        if (gradeToSave) {
          saveToApi("assessment-grades", gradeToSave, method, gradeToSave.id);
        }
      }
    },
    [tugasList],
  );

  const handleUpdateSubmission = useCallback(
    async (submission: TugasSubmission) => {
      setTugasSubmissions((prev) =>
        prev.map((s) => (s.id === submission.id ? submission : s)),
      );
      saveToApi("tugas-submissions", submission, "PUT", submission.id);
    },
    [],
  );

  const handleDeleteSubmission = useCallback(
    async (id: string) => {
      console.log("App: handleDeleteSubmission", id);
      const submission = tugasSubmissions.find((s) => s.id === id);
      const originalSubmissions = [...tugasSubmissions];
      setTugasSubmissions((prev) => prev.filter((s) => s.id !== id));
      const result = await saveToApi("tugas-submissions", null, "DELETE", id);
      if (result && result.error) {
        setTugasSubmissions(originalSubmissions);
        console.error("Gagal menghapus hasil tugas:", result);
      } else if (submission) {
        // Remove score from assessment grade
        const gradeId = `tugas-${submission.tugasId}`;
        setAssessmentGrades((prev) => {
          const existing = prev.find((g) => g.id === gradeId);
          if (existing) {
            const newScores = { ...existing.scores };
            delete newScores[submission.studentId];
            const updatedGrade = { ...existing, scores: newScores };
            saveToApi("assessment-grades", updatedGrade, "PUT", gradeId);
            return prev.map((g) => (g.id === gradeId ? updatedGrade : g));
          }
          return prev;
        });
      }
    },
    [tugasSubmissions],
  );

  const handleStartTugasSession = useCallback(async (session: TugasSession) => {
    setTugasSessions((prev) => {
      const filtered = prev.filter((s) => s.id !== session.id);
      return [...filtered, session];
    });
    saveToApi("tugasSessions", session, "POST");
  }, []);

  const handleEndTugasSession = useCallback(async (sessionId: string) => {
    setTugasSessions((prev) => prev.filter((s) => s.id !== sessionId));
    saveToApi("tugasSessions", null, "DELETE", sessionId);
  }, []);

  const handleUpdateGrades = async (newGrades: AssessmentGrade[]) => {
    const oldGrades = [...(assessmentGrades || [])];
    setAssessmentGrades(newGrades || []);

    try {
      const added = (newGrades || []).filter((g) => !oldGrades.some((old) => old.id === g.id));
      const deleted = oldGrades.filter((old) => !(newGrades || []).some((g) => g.id === old.id));
      const updated = (newGrades || []).filter((g) => {
        const old = oldGrades.find((o) => o.id === g.id);
        return old && JSON.stringify(old) !== JSON.stringify(g);
      });

      const promises: Promise<any>[] = [];
      if (added.length > 0) {
        promises.push(...added.map((a) => saveToApi("assessment-grades", a, "POST", a.id)));
      }
      if (deleted.length > 0) {
        promises.push(...deleted.map((d) => saveToApi("assessment-grades", null, "DELETE", d.id)));
      }
      if (updated.length > 0) {
        promises.push(...updated.map((u) => saveToApi("assessment-grades", u, "PUT", u.id)));
      }

      await Promise.all(promises);
    } catch (error) {
      console.error("Error updating grades:", error);
    }
  };

  const handleUpdateSasGrades = async (newGrades: SasGrade[]) => {
    const oldGrades = [...(sasGrades || [])];
    setSasGrades(newGrades || []);

    try {
      const added = (newGrades || []).filter((g) => !oldGrades.some((old) => old.id === g.id));
      const deleted = oldGrades.filter((old) => !(newGrades || []).some((g) => g.id === old.id));
      const updated = (newGrades || []).filter((g) => {
        const old = oldGrades.find((o) => o.id === g.id);
        return old && JSON.stringify(old) !== JSON.stringify(g);
      });

      const promises: Promise<any>[] = [];
      if (added.length > 0) {
        promises.push(...added.map((a) => saveToApi("sas-grades", a, "POST", a.id)));
      }
      if (deleted.length > 0) {
        promises.push(...deleted.map((d) => saveToApi("sas-grades", null, "DELETE", d.id)));
      }
      if (updated.length > 0) {
        promises.push(...updated.map((u) => saveToApi("sas-grades", u, "PUT", u.id)));
      }

      await Promise.all(promises);
    } catch (error) {
      console.error("Error updating SAS grades:", error);
    }
  };

  const handleUpdateFormativeTP = async (newGrades: FormativeGradeTP[]) => {
    const oldGrades = [...(formativeGradesTP || [])];
    setFormativeGradesTP(newGrades || []);

    const added = (newGrades || []).filter((g) => !oldGrades.some((old) => old.id === g.id));
    const deleted = oldGrades.filter((old) => !(newGrades || []).some((g) => g.id === old.id));
    const updated = (newGrades || []).filter((g) => {
      const old = oldGrades.find((o) => o.id === g.id);
      return old && JSON.stringify(old) !== JSON.stringify(g);
    });

    if (added.length > 0) {
      console.log("Adding new formative grades:", added);
      added.forEach((a) => saveToApi("formative-grades", a, "POST", a.id));
    }
    if (deleted.length > 0) {
      console.log("Deleting formative grades:", deleted);
      deleted.forEach((d) => saveToApi("formative-grades", null, "DELETE", d.id));
    }
    if (updated.length > 0) {
      console.log("Updating formative grades:", updated);
      updated.forEach((u) => saveToApi("formative-grades", u, "PUT", u.id));
    }
  };

  const handleUpdateSchedules = async (newSchedules: ScheduleEntry[]) => {
    const oldSchedules = [...(schedules || [])];
    setSchedules(newSchedules || []);

    const added = (newSchedules || []).filter((s) => !oldSchedules.some((old) => old.id === s.id));
    const deleted = oldSchedules.filter((old) => !(newSchedules || []).some((s) => s.id === old.id));
    const updated = (newSchedules || []).filter((s) => {
      const old = oldSchedules.find((oldSch) => oldSch.id === s.id);
      return old && JSON.stringify(old) !== JSON.stringify(s);
    });

    if (added.length > 0) {
      added.forEach((a) => saveToApi("schedules", a, "POST", a.id));
    }
    if (deleted.length > 0) {
      deleted.forEach((d) => saveToApi("schedules", null, "DELETE", d.id));
    }
    if (updated.length > 0) {
      updated.forEach((u) => saveToApi("schedules", u, "PUT", u.id));
    }
  };

  const handleUpdateHomeroomAttendance = async (newRecords: AttendanceRecord[]) => {
    const oldRecords = [...(homeroomAttendance || [])];
    setHomeroomAttendance(newRecords || []);

    const added = (newRecords || []).filter(
      (r) => !oldRecords.some((old) => old.id === r.id),
    );
    const deleted = oldRecords.filter(
      (old) => !(newRecords || []).some((r) => r.id === old.id),
    );
    const updated = (newRecords || []).filter((r) => {
      const old = oldRecords.find((o) => o.id === r.id);
      return (
        old && (old.status !== r.status || old.description !== r.description)
      );
    });

    if (added.length > 0) {
      added.forEach((r) => saveToApi("homeroomAttendance", r, "POST"));
    }
    if (deleted.length > 0) {
      deleted.forEach((d) => saveToApi("homeroomAttendance", null, "DELETE", d.id));
    }
    if (updated.length > 0) {
      updated.forEach((u) => saveToApi("homeroomAttendance", u, "PUT", u.id));
    }
  };

  const handleUpdateAttendance = async (newRecords: AttendanceRecord[]) => {
    const oldRecords = [...(attendanceRecords || [])];
    setAttendanceRecords(newRecords || []);

    // Efficiency: only sync differences to Firebase
    const added = (newRecords || []).filter(
      (r) => !oldRecords.some((old) => old.id === r.id),
    );
    const deleted = oldRecords.filter(
      (old) => !(newRecords || []).some((r) => r.id === old.id),
    );
    const updated = (newRecords || []).filter((r) => {
      const old = oldRecords.find((o) => o.id === r.id);
      return (
        old && (old.status !== r.status || old.description !== r.description)
      );
    });

    if (added.length > 0) {
      added.forEach((r) => saveToApi("attendance", r, "POST"));
    }
    if (deleted.length > 0) {
      deleted.forEach((d) => saveToApi("attendance", null, "DELETE", d.id));
    }
    if (updated.length > 0) {
      updated.forEach((u) => saveToApi("attendance", u, "PUT", u.id));
    }
  };

  const handleUpdateTeachers = async (newTeachers: Teacher[]) => {
    const oldTeachers = [...teachers];

    if (newTeachers.length > oldTeachers.length) {
      // Added
      const added = newTeachers.find(
        (t) => t.id === "" || !oldTeachers.some((old) => old.id === t.id),
      );
      if (added) {
        const { id, ...dataToSave } = added; // Remove temporary ID
        const result = await saveToApi("teachers", dataToSave, "POST");
        if (!result.error) {
          // Update state with the real ID from DB
          setTeachers((prev) => {
            const updated = [...prev];
            const index = updated.findIndex((t) => t.id === "");
            if (index !== -1) updated[index] = result;
            return updated;
          });
        }
      }
    } else if (newTeachers.length < oldTeachers.length) {
      // Deleted
      const deleted = oldTeachers.find(
        (old) => !newTeachers.some((t) => t.id === old.id),
      );
      if (deleted) {
        await saveToApi("teachers", null, "DELETE", deleted.id);
        setTeachers(newTeachers);
      }
    } else {
      // Updated
      const updated = newTeachers.find((t) => {
        const old = oldTeachers.find((o) => o.id === t.id);
        return old && JSON.stringify(old) !== JSON.stringify(t);
      });
      if (updated) {
        await saveToApi("teachers", updated, "PUT", updated.id);
        setTeachers(newTeachers);
      }
    }

    // Optimistic update for UI (except for new items which need DB ID)
    if (newTeachers.length <= oldTeachers.length) {
      setTeachers(newTeachers);
    } else {
      // For new items, we temporarily add it to UI, it will be replaced by DB version
      setTeachers(newTeachers);
    }
  };

  // Sync state changes to API - Commented out problematic automatic syncs
  // These were causing redundant POST requests and potential errors
  /*
  useEffect(() => { if (!isLoading && teachers.length > 0) saveToApi('teachers', teachers[0]); }, [teachers]); 
  useEffect(() => { if (!isLoading && schoolSettings.schoolName) saveToApi('settings', schoolSettings); }, [schoolSettings]);
  */

  // GENERATOR DATA DUMMY (Siswa, Jadwal, Agenda, Absensi, Penilaian)
  useEffect(() => {
    // Auto-generator data dummy telah dipindahkan ke menu DatabaseManager
    if (false) {
      console.log("Initializing Comprehensive Dummy Data...");

      // 1. Generate Siswa untuk SEMUA kelas (10 siswa per rombel)
      const dummyStudents: Student[] = [];
      const firstNames = [
        "Aditya",
        "Bima",
        "Citra",
        "Dian",
        "Eka",
        "Fajar",
        "Ghani",
        "Hesty",
        "Indra",
        "Jati",
        "Kurnia",
        "Laras",
        "Maulana",
        "Nadia",
        "Okta",
        "Pandu",
        "Qonita",
        "Rizky",
        "Sari",
        "Taufik",
        "Agus",
        "Bambang",
        "Caca",
        "Doni",
        "Endang",
        "Fitri",
        "Guntur",
        "Heri",
        "Ika",
        "Joni",
      ];
      const lastNames = [
        "Saputra",
        "Wicaksono",
        "Lestari",
        "Putri",
        "Kurniawan",
        "Hidayat",
        "Pratama",
        "Sari",
        "Utami",
        "Setiawan",
        "Nugroho",
        "Wijaya",
        "Ramadhani",
        "Fitriani",
        "Sanjaya",
      ];

      initialClasses.forEach((cls) => {
        const count = 10;
        for (let i = 0; i < count; i++) {
          const firstName =
            firstNames[Math.floor(Math.random() * firstNames.length)];
          const lastName =
            lastNames[Math.floor(Math.random() * lastNames.length)];
          const name = `${firstName} ${lastName}`;
          dummyStudents.push({
            id: `s-${cls}-${i}`,
            nis: `2223${cls.replace(/\D/g, "")}${100 + i}`,
            name: name,
            class: cls,
            gender: Math.random() > 0.5 ? "Laki-laki" : "Perempuan",
            status: "Aktif",
          });
        }
      });
      setStudents(dummyStudents);

      // 2. Generate Jadwal Pelajaran (Schedule) - Monday to Friday
      const dummySchedules: ScheduleEntry[] = [
        {
          id: "sch1",
          day: "Senin",
          period: 1,
          time: "07:30",
          subject: "Informatika",
          class: "9A",
        },
        {
          id: "sch2",
          day: "Senin",
          period: 2,
          time: "08:10",
          subject: "Informatika",
          class: "9A",
        },
        {
          id: "sch3",
          day: "Senin",
          period: 3,
          time: "08:50",
          subject: "Informatika",
          class: "9B",
        },
        {
          id: "sch4",
          day: "Senin",
          period: 4,
          time: "09:30",
          subject: "Informatika",
          class: "9B",
        },
        {
          id: "sch5",
          day: "Selasa",
          period: 1,
          time: "07:30",
          subject: "Informatika",
          class: "9C",
        },
        {
          id: "sch6",
          day: "Selasa",
          period: 2,
          time: "08:10",
          subject: "Informatika",
          class: "9C",
        },
        {
          id: "sch7",
          day: "Selasa",
          period: 5,
          time: "10:30",
          subject: "Informatika",
          class: "9D",
        },
        {
          id: "sch8",
          day: "Selasa",
          period: 6,
          time: "11:10",
          subject: "Informatika",
          class: "9D",
        },
        {
          id: "sch9",
          day: "Rabu",
          period: 3,
          time: "08:50",
          subject: "Informatika",
          class: "9E",
        },
        {
          id: "sch10",
          day: "Rabu",
          period: 4,
          time: "09:30",
          subject: "Informatika",
          class: "9E",
        },
        {
          id: "sch11",
          day: "Rabu",
          period: 7,
          time: "12:30",
          subject: "Informatika",
          class: "9F",
        },
        {
          id: "sch12",
          day: "Kamis",
          period: 1,
          time: "07:30",
          subject: "Informatika",
          class: "9G",
        },
        {
          id: "sch13",
          day: "Kamis",
          period: 2,
          time: "08:10",
          subject: "Informatika",
          class: "9G",
        },
        {
          id: "sch14",
          day: "Kamis",
          period: 5,
          time: "10:30",
          subject: "Informatika",
          class: "9H",
        },
        {
          id: "sch15",
          day: "Jumat",
          period: 1,
          time: "07:30",
          subject: "Informatika",
          class: "9I",
        },
        {
          id: "sch16",
          day: "Jumat",
          period: 2,
          time: "08:10",
          subject: "Informatika",
          class: "9J",
        },
        {
          id: "sch17",
          day: "Jumat",
          period: 3,
          time: "08:50",
          subject: "Informatika",
          class: "9K",
        },
      ];
      setSchedules(dummySchedules);

      // 3. Generate Agenda, Absensi, dan Assessment Grades Historis (1 Jan - Sekarang)
      const dummyAgendas: AgendaEntry[] = [];
      const dummyAttendance: AttendanceRecord[] = [];
      const dummyAssessmentGrades: AssessmentGrade[] = [];
      const dummyFormativeGradesTP: FormativeGradeTP[] = [];
      const dummyTugas: Tugas[] = [];
      const dummyTugasSubmissions: TugasSubmission[] = [];

      // Ensure we have at least one learning objective
      let currentLearningObjectives = [...learningObjectives];
      if (currentLearningObjectives.length === 0) {
        currentLearningObjectives = [
          {
            id: "tp-1",
            subject: "Informatika",
            code: "TP.7.1",
            description: "Memahami konsep dasar informatika",
            semester: "Ganjil",
            academicYear: "2023/2024",
          },
          {
            id: "tp-2",
            subject: "Informatika",
            code: "TP.8.1",
            description: "Menerapkan algoritma sederhana",
            semester: "Ganjil",
            academicYear: "2023/2024",
          },
          {
            id: "tp-3",
            subject: "Informatika",
            code: "TP.9.1",
            description: "Membuat program sederhana",
            semester: "Ganjil",
            academicYear: "2023/2024",
          },
        ];
        setLearningObjectives(currentLearningObjectives);
      }

      const startDate = new Date("2025-01-01");
      const today = new Date();
      const dayNamesMap = [
        "Minggu",
        "Senin",
        "Selasa",
        "Rabu",
        "Kamis",
        "Jumat",
        "Sabtu",
      ];

      const topics = [
        "Pengenalan Algoritma Dasar",
        "Logika Pemrograman",
        "Struktur Data Larik",
        "Dasar-dasar Internet",
        "Keamanan Digital",
        "Pemrosesan Kata Tingkat Lanjut",
        "Analisis Data Spreadsheet",
        "Pembuatan Website Sederhana",
        "Dampak Sosial Informatika",
        "Etika Berinternet",
        "Kecerdasan Buatan Dasar",
        "Robotika Sederhana",
      ];
      const formativeLevelsArr: FormativeLevel[] = [
        "Awal Berkembang",
        "Layak",
        "Mahir",
      ];

      for (
        let d = new Date(startDate);
        d <= today;
        d.setDate(d.getDate() + 1)
      ) {
        const currentDayName = dayNamesMap[d.getDay()];
        if (currentDayName === "Minggu" || currentDayName === "Sabtu") continue;

        const relevantSchedules = dummySchedules.filter(
          (s) => s.day === currentDayName,
        );

        const classMap = new Map<string, { start: number; end: number }>();
        relevantSchedules.forEach((s) => {
          if (!classMap.has(s.class)) {
            classMap.set(s.class, { start: s.period, end: s.period });
          } else {
            const existing = classMap.get(s.class)!;
            if (s.period < existing.start) existing.start = s.period;
            if (s.period > existing.end) existing.end = s.period;
          }
        });

        classMap.forEach((periods, cls) => {
          const dateStr = d.toISOString().split("T")[0];
          const agendaId = `agenda-${cls}-${dateStr}-${periods.start}`;
          const tp =
            currentLearningObjectives[
              Math.floor(Math.random() * currentLearningObjectives.length)
            ];
          const topic = topics[Math.floor(Math.random() * topics.length)];

          // GENERASI activityType yang lebih merata untuk memastikan data nilai
          let activityType: ActivityType;
          const randActivity = Math.random();
          if (randActivity < 0.3) activityType = ActivityType.KBM_DAN_QUIZ;
          else if (randActivity < 0.6) activityType = ActivityType.SUMATIF;
          else if (randActivity < 0.8) activityType = ActivityType.SAS;
          else activityType = ActivityType.KBM;

          const newAgendaEntry: AgendaEntry = {
            id: agendaId,
            date: dateStr,
            class: cls,
            subject: "Informatika", // Assuming dummy agendas are for Informatics
            topic: `${topic} (${dateStr})`,
            activityType: activityType,
            startPeriod: periods.start,
            endPeriod: periods.end,
            tpId: tp.id,
            tpDescription: tp.description,
            notes: "Data administratif otomatis.",
            status: "Selesai",
          };
          dummyAgendas.push(newAgendaEntry);

          // Generate Absensi
          const classStudents = dummyStudents.filter((s) => s.class === cls);
          classStudents.forEach((student) => {
            const r = Math.random();
            let status = AttendanceStatus.Hadir;
            if (r > 0.98) status = AttendanceStatus.Alpa;
            else if (r > 0.95) status = AttendanceStatus.Sakit;
            else if (r > 0.92) status = AttendanceStatus.Izin;

            dummyAttendance.push({
              id: `att-${agendaId}-${student.id}`,
              studentId: student.id,
              agendaId: agendaId,
              date: dateStr,
              status: status,
            });
          });

          // Generate Assessment Grades (Tugas, Sumatif, SAS)
          if (
            [
              ActivityType.KBM_DAN_QUIZ,
              ActivityType.SUMATIF,
              ActivityType.SAS,
            ].includes(activityType)
          ) {
            const scores: Record<string, number> = {};
            classStudents.forEach((student) => {
              scores[student.id] =
                Math.floor(Math.random() * (95 - 60 + 1)) + 60; // Scores between 60-95
            });
            dummyAssessmentGrades.push({
              id: `${newAgendaEntry.class}-${newAgendaEntry.tpId}-${newAgendaEntry.activityType}-${dateStr}-${periods.start}`, // More unique ID
              class: newAgendaEntry.class,
              tpId: newAgendaEntry.tpId || "",
              activityType: newAgendaEntry.activityType!,
              date: newAgendaEntry.date,
              scores: scores,
            });
          }
        });
      }
      setAgendas(dummyAgendas);
      setAttendanceRecords(dummyAttendance);
      setAssessmentGrades(dummyAssessmentGrades);

      // Generate Formative Grades (per TP and Class) - only for 'Informatika' subject
      learningObjectives
        .filter((obj) => obj.subject?.trim().toLowerCase() === "informatika")
        .forEach((tp) => {
          initialClasses.forEach((cls) => {
            const classStudents = dummyStudents.filter((s) => s.class === cls);
            const levels: Record<string, FormativeLevel> = {};
            classStudents.forEach((student) => {
              let selectedLevel: FormativeLevel;
              const randBias = Math.random();
              if (randBias < 0.1)
                selectedLevel = "Awal Berkembang"; // 10% Awal Berkembang
              else if (randBias > 0.9)
                selectedLevel = "Mahir"; // 10% Mahir
              else selectedLevel = Math.random() > 0.5 ? "Layak" : "Mahir"; // 80% split between Layak and Mahir
              levels[student.id] = selectedLevel;
            });
            dummyFormativeGradesTP.push({
              id: `${cls}-${tp.id}`,
              class: cls,
              tpId: tp.id,
              levels: levels,
            });
          });
        });
      setFormativeGradesTP(dummyFormativeGradesTP);

      // Generate Dummy Tugas
      const informaticsTeacher =
        teachers.find((t) => t.subject === "Informatika") || teachers[0];
      if (informaticsTeacher) {
        const tugas1: Tugas = {
          id: "tugas-1",
          teacherId: informaticsTeacher.id,
          subject: "Informatika",
          title: "Pengenalan Algoritma",
          description:
            "Tugas dasar mengenai konsep algoritma dalam kehidupan sehari-hari.",
          class: "7A",
          dueDate: new Date(
            today.getTime() + 7 * 24 * 60 * 60 * 1000,
          ).toISOString(),
          questions: [
            {
              id: "q1",
              text: "Apa yang dimaksud dengan algoritma?",
              type: "essay",
              points: 100,
              options: [],
              correctAnswerIndex: -1,
            },
          ],
          type: TugasType.TUGAS,
          createdAt: today.toISOString(),
          isActive: true,
        };
        const tugas2: Tugas = {
          id: "tugas-2",
          teacherId: informaticsTeacher.id,
          subject: "Informatika",
          title: "Tugas Logika Pemrograman",
          description: "Uji pemahaman logika dasar pemrograman.",
          class: "7A",
          dueDate: new Date(
            today.getTime() + 2 * 24 * 60 * 60 * 1000,
          ).toISOString(),
          questions: [
            {
              id: "q1",
              text: "Simbol belah ketupat pada flowchart melambangkan?",
              type: "multiple-choice",
              options: ["Start", "Process", "Decision", "End"],
              correctAnswerIndex: 2,
              points: 50,
            },
            {
              id: "q2",
              text: "Urutan langkah-langkah logis disebut?",
              type: "multiple-choice",
              options: ["Program", "Algoritma", "Variabel", "Loop"],
              correctAnswerIndex: 1,
              points: 50,
            },
          ],
          type: TugasType.TUGAS,
          createdAt: today.toISOString(),
          isActive: true,
        };
        dummyTugas.push(tugas1, tugas2);
        setTugasList(dummyTugas);
      }

      // Save dummy data to API
      saveToApi("students", dummyStudents);
      saveToApi("schedules", dummySchedules);
      saveToApi("agendas", dummyAgendas);
      saveToApi("attendance", dummyAttendance);
      saveToApi("assessment-grades", dummyAssessmentGrades);
      saveToApi("formative-grades", dummyFormativeGradesTP);
      saveToApi("tugas", dummyTugas);
    }
  }, [learningObjectives, students.length, isLoading]);

  // Restore localStorage sync effects
  useEffect(() => {
    localStorage.setItem("smpn1_classes", JSON.stringify(classes));
  }, [classes]);
  useEffect(() => {
    localStorage.setItem("smpn1_teachers", JSON.stringify(teachers));
  }, [teachers]);
  useEffect(() => {
    localStorage.setItem("smpn1_tp", JSON.stringify(learningObjectives));
  }, [learningObjectives]);
  useEffect(() => {
    localStorage.setItem("smpn1_students", JSON.stringify(students));
  }, [students]);
  useEffect(() => {
    localStorage.setItem("smpn1_agendas", JSON.stringify(agendas));
  }, [agendas]);
  useEffect(() => {
    localStorage.setItem("smpn1_attendance", JSON.stringify(attendanceRecords));
  }, [attendanceRecords]);
  useEffect(() => {
    localStorage.setItem("smpn1_homeroom_attendance", JSON.stringify(homeroomAttendance));
  }, [homeroomAttendance]);
  useEffect(() => {
    localStorage.setItem("smpn1_schedules", JSON.stringify(schedules));
  }, [schedules]);
  useEffect(() => {
    localStorage.setItem(
      "smpn1_assessment_grades",
      JSON.stringify(assessmentGrades),
    );
  }, [assessmentGrades]);
  useEffect(() => {
    localStorage.setItem(
      "smpn1_formative_tp",
      JSON.stringify(formativeGradesTP),
    );
  }, [formativeGradesTP]);
  useEffect(() => {
    localStorage.setItem("smpn1_notifications", JSON.stringify(notifications));
  }, [notifications]);
  useEffect(() => {
    localStorage.setItem("smpn1_settings", JSON.stringify(schoolSettings));
  }, [schoolSettings]);
  useEffect(() => {
    localStorage.setItem("smpn1_waliRombel", JSON.stringify(waliRombel));
  }, [waliRombel]);

  const handleLoginSubmit = async (
    username: string,
    password: string,
  ): Promise<{ success: boolean; error?: string }> => {
    // 0. Super Admin static login
    if (username === "ega" && password === "ega") {
      handleLoginSuccess(UserRole.Admin, {
        id: "admin",
        name: "Super Admin Ega",
      });
      return { success: true };
    }

    try {
      // Pastikan ada DB sebelum query
      if (!db) return { success: false, error: "Database belum siap." };

      // 1. Cek di daftar guru (Gunakan query tertarget, jangan load semua)
      const teacherQ = query(
        collection(db, "teachers"),
        where("username", "==", username),
        where("password", "==", password),
        limit(1),
      );
      const teacherSnap = await getDocs(teacherQ);

      if (!teacherSnap.empty) {
        const teacherData = teacherSnap.docs[0].data() as Teacher;
        handleLoginSuccess(UserRole.Guru, teacherData);
        return { success: true };
      }

      // 2. Query ke students collection berdasarkan NIS
      const studentQ = query(
        collection(db, "students"),
        where("nis", "==", username),
        limit(1),
      );
      const studentSnap = await getDocs(studentQ);

      if (!studentSnap.empty) {
        const studentData = studentSnap.docs[0].data() as Student;
        // Verify password is nis (as per original logic)
        if (studentData.nis === password) {
          // If login success, maybe push to state immediately to avoid re-fetch?
          // Since students state might not have us, we could pre-populate it
          setStudents((prev) => {
            if (!prev.find((s) => s.id === studentData.id)) {
              return [...prev, studentData];
            }
            return prev;
          });
          handleLoginSuccess(UserRole.Siswa, studentData);
          return { success: true };
        }
      }

      // 3. Optional: Query fallback for teacher if not heavily cached? (Already handled by critical load)
    } catch (error: any) {
      console.error("Login error:", error);
      checkQuotaError(error);
      return {
        success: false,
        error: "Terjadi kesalahan saat memeriksa data.",
      };
    }

    return { success: false, error: "Username atau Password salah!" };
  };

  const handleFetchOnDemand = useCallback(
    async (
      target:
        | "learningObjectives"
        | "agendas"
        | "learningContents"
        | "attendanceRecords",
    ) => {
      if (!db || !activeUser || role !== UserRole.Siswa) return;

      const studentClass = (activeUser as Student).class;
      const key = `${activeUser.id}_${target}`;

      if (fetchedOnDemandKeysRef.current.has(key)) {
        return;
      }
      fetchedOnDemandKeysRef.current.add(key);

      // Check if already fetched to avoid duplicate reads
      if (target === "learningObjectives" && learningObjectives.length > 0)
        return;
      if (target === "agendas" && agendas.some(a => a.class === studentClass))
        return;
      if (target === "learningContents" && learningContents.length > 0)
        return;

      try {
        let data: any[] = [];

        if (target === "learningObjectives") {
          const snap = await getDocs(collection(db, "learningObjectives"));
          data = snap.docs.map((doc) => {
            const d = doc.data() as any;
            if (!d.id) d.id = doc.id;
            return d;
          });
          setLearningObjectives(data as LearningObjective[]);
          localStorage.setItem("smpn1_tp", JSON.stringify(data));
        } else if (target === "agendas") {
          const q = query(
            collection(db, "agendas"),
            where("class", "==", studentClass),
          );
          const snap = await getDocs(q);
          data = snap.docs.map((doc) => {
            const d = doc.data() as any;
            if (!d.id) d.id = doc.id;
            return d;
          });
          setAgendas((prev) => {
            const filtered = prev.filter((a) => a.class !== studentClass);
            const updated = [...filtered, ...data];
            localStorage.setItem("smpn1_agendas", JSON.stringify(updated));
            return updated;
          });
        } else if (target === "learningContents") {
          const snap = await getDocs(collection(db, "learningContents"));
          data = snap.docs.map((doc) => {
            const d = doc.data() as any;
            if (!d.id) d.id = doc.id;
            return d;
          });
          setLearningContents(data as LearningContent[]);
          localStorage.setItem("smpn1_learningContents", JSON.stringify(data));
        } else if (target === "attendanceRecords") {
          const q1 = query(
            collection(db, "attendanceRecords"),
            where("studentId", "==", activeUser.id),
          );
          const snap1 = await getDocs(q1);
          data = snap1.docs.map((doc) => {
            const d = doc.data() as any;
            if (!d.id) d.id = doc.id;
            return d;
          });

          const activeStudent = activeUser as Student;
          if (activeStudent.nis && activeStudent.nis !== activeUser.id) {
            const q2 = query(
              collection(db, "attendanceRecords"),
              where("studentId", "==", activeStudent.nis),
            );
            const snap2 = await getDocs(q2);
            const data2 = snap2.docs.map((doc) => {
              const d = doc.data() as any;
              if (!d.id) d.id = doc.id;
              return d;
            });
            data2.forEach((item2) => {
              if (!data.some((d) => d.id === item2.id)) {
                data.push(item2);
              }
            });
          }

          setAttendanceRecords((prev) => {
            const filtered = prev.filter(
              (r) =>
                r.studentId !== activeUser.id &&
                r.studentId !== (activeUser as Student).nis,
            );
            const updated = [...filtered, ...data];
            localStorage.setItem("smpn1_attendance", JSON.stringify(updated));
            return updated;
          });
        }
      } catch (err: any) {
        console.error(`Error fetching ${target} on demand:`, err);
        checkQuotaError(err);
      }
    },
    [db, activeUser, role, learningObjectives.length, agendas, learningContents.length],
  );

  const handleLoginSuccess = (userRole: UserRole, user: any) => {
    setRole(userRole);
    setActiveUser(user);
    setIsAuthenticated(true);

    if (userRole === UserRole.Siswa) {
      setActiveView(View.SubjectSelection);
    } else {
      setActiveView(View.Dashboard);
    }

    localStorage.setItem("smpn1_userRole", userRole);
    localStorage.setItem("smpn1_activeUser", JSON.stringify(user));
    localStorage.setItem("smpn1_isAuthenticated", "true");
  };

  const toggleTheme = () => {
    setThemeMode((prev) => {
      if (prev === "system") return "light";
      if (prev === "light") return "dark";
      return "system";
    });
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setActiveUser(null);
    setActiveView(View.Dashboard);

    localStorage.removeItem("smpn1_userRole");
    localStorage.removeItem("smpn1_activeUser");
    localStorage.removeItem("smpn1_isAuthenticated");
  };

  const logProfileAccess = async (studentId: string, studentClass?: string) => {
    console.log("logProfileAccess called for studentId:", studentId);
    if (!activeUser) {
      console.log("logProfileAccess: activeUser is null");
      return;
    }

    try {
      const student = students.find((s) => s.id === studentId);
      const sClass = studentClass || student?.class || "Unknown";

      console.log("logProfileAccess: activeUser:", activeUser);
      // Get IP
      let ip = "Unknown";
      try {
        const ipRes = await fetch("https://api.ipify.org?format=json");
        const ipData = await ipRes.json();
        ip = ipData.ip;
      } catch (e) {
        console.error("Failed to fetch IP", e);
      }

      // Get OS and Device
      const ua = navigator.userAgent;
      let os = "Unknown OS";
      if (ua.indexOf("Win") !== -1) os = "Windows";
      if (ua.indexOf("Mac") !== -1) os = "MacOS";
      if (ua.indexOf("X11") !== -1) os = "UNIX";
      if (ua.indexOf("Linux") !== -1) os = "Linux";
      if (/Android/.test(ua)) os = "Android";
      if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";

      let device = "Desktop";
      if (/Mobi|Android/i.test(ua)) device = "Mobile";
      if (/Tablet|iPad/i.test(ua)) device = "Tablet";

      // Try to extract brand/model if possible (very basic)
      const match = ua.match(/\(([^)]+)\)/);
      if (match && match[1]) {
        device += ` (${match[1].split(";")[0]})`;
      }

      let accessorNis = "";
      if (role === UserRole.Siswa) accessorNis = (activeUser as any).nis || "";
      else if (role === UserRole.Guru)
        accessorNis = (activeUser as any).nip || "";
      else accessorNis = "Admin";

      const newLog: ProfileAccessLog = {
        id: crypto.randomUUID(),
        studentId,
        studentClass: sClass,
        accessorId: activeUser.id,
        accessorName: activeUser.name,
        accessorNis,
        accessorRole: role,
        timestamp: new Date().toISOString(),
        device,
        os,
        ip,
      };

      console.log("logProfileAccess: newLog:", newLog);
      setProfileAccessLogs((prev) => [...prev, newLog]);
      
      // 1. Save to Firebase (Real-time sync source)
      const res = await saveToApi("profile-access-logs", newLog, "POST");
      console.log("logProfileAccess: saveToApi response:", res);

      // 2. Save to Postgres (Primary storage)
      fetch(`${API_BASE_URL}/profile-access-logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newLog)
      }).catch(err => console.error("Error saving log to Postgres API:", err));
    } catch (error) {
      console.error("Error logging profile access:", error);
    }
  };

  const deleteProfileAccessLog = async (id: string) => {
    try {
      // 1. Delete from Firebase (Real-time sync source)
      await saveToApi("profile-access-logs", null, "DELETE", id);
      
      // 2. Delete from Postgres (Primary storage)
      const res = await fetch(`${API_BASE_URL}/profile-access-logs/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete log from primary storage");
      
      // 3. Update local state (optimistic or for non-realtime fallback)
      setProfileAccessLogs((prev) => prev.filter((log) => log.id !== id));
    } catch (error) {
      console.error("Error deleting profile access log:", error);
    }
  };

  const clearAllProfileAccessLogs = async () => {
    try {
      setProfileAccessLogs([]); // Optimistic update
      localStorage.setItem("smpn1_profileAccessLogs", JSON.stringify([]));
      
      // 1. Clear from Postgres
      const res = await fetch(`${API_BASE_URL}/clear/profileAccessLogs`, {
        method: "DELETE",
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to clear all logs from primary storage");
      }

      // 2. Clear from Firebase (Real-time sync source)
      // We'll do this in a single batch if possible, or loop if many
      const q = query(collection(db, "profileAccessLogs"));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        // Delete in batches of 500 to stay within Firestore limits
        let docs = snapshot.docs;
        while (docs.length > 0) {
          const batch = writeBatch(db);
          const chunk = docs.slice(0, 500);
          docs = docs.slice(500);
          
          chunk.forEach((doc) => {
            batch.delete(doc.ref);
          });
          
          await batch.commit();
          console.log(`Deleted sub-batch of ${chunk.length} from Firebase`);
        }
      }
      
      alert("Semua riwayat akses berhasil dibersihkan.");
    } catch (error) {
      console.error("Error clearing all profile access logs:", error);
      alert("Gagal membersihkan riwayat: " + error);
      // Reload on failure
      window.location.reload();
    }
  };

  const navigateTo = (view: View, params?: any) => {
    if (view === View.Agenda && params?.tab) {
      setTargetAgendaTab(params.tab);
    } else {
      setTargetAgendaTab("input");
    }

    if (view === View.Attendance && params?.tab) {
      setTargetAttendanceTab(params.tab);
    } else {
      setTargetAttendanceTab("today");
    }

    if ((view === View.Grading || view === View.Agenda) && params?.agenda) {
      setTargetGradingAgenda(params.agenda);
    } else {
      setTargetGradingAgenda(null);
    }

    if (view === View.Tugas && params?.tugasId) {
      setTargetTugasId(params.tugasId);
    } else {
      setTargetTugasId(null);
    }

    if (view === View.StudentProfile && params?.student) {
      setSelectedStudentForProfile(params.student);
      logProfileAccess(params.student.id, params.student.class);
    } else {
      setSelectedStudentForProfile(null);
    }

    setActiveView(view);
  };

  const renderView = () => {
    if (
      role === UserRole.Siswa &&
      (quotaError || schoolSettings.isStudentMaintenanceMode)
    ) {
      return (
        <MaintenanceView
          message={
            quotaError ||
            "Halaman siswa sedang dalam mode maintenance oleh admin sekolah. Silakan kembali lagi nanti."
          }
          onRetry={handleManualRetry}
        />
      );
    }

    if (
      role === UserRole.Siswa &&
      (activeView === View.StudentProfile || activeView === View.Materi || activeView === View.StudentGrade)
    ) {
      return (
        <StudentProfileView
          student={activeUser as Student}
          students={students}
          agendas={agendas}
          attendanceRecords={attendanceRecords}
          assessmentGrades={assessmentGrades}
          sasGrades={sasGrades}
          formativeGradesTP={formativeGradesTP}
          objectives={learningObjectives}
          schedules={schedules}
          teachers={teachers}
          teacherSubject={selectedSubjectForStudent}
          onBack={() => setActiveView(View.SubjectSelection)}
          tugas={tugasList}
          tugasSubmissions={tugasSubmissions}
          learningContents={learningContents}
          onSaveTugas={handleSaveTugas}
          onDeleteTugas={handleDeleteTugas}
          onSubmitTugas={handleSubmitTugas}
          role={role}
          schoolSettings={schoolSettings}
          onFetchOnDemand={handleFetchOnDemand}
          initialTab={activeView === View.StudentGrade ? 'nilai' : 'profil'}
          onTabChange={(tab) => {
            setActiveView(tab === 'nilai' ? View.StudentGrade : View.StudentProfile);
          }}
        />
      );
    }

    switch (activeView) {
      case View.Dashboard:
        return (
          <DashboardView
            role={role}
            activeUser={activeUser}
            schedules={schedules}
            agendas={agendas}
            students={students}
            teachers={teachers}
            onNavigate={navigateTo}
            onUpdateAgenda={(updated) =>
              setAgendas((prev) =>
                prev.map((a) => (a.id === updated.id ? updated : a)),
              )
            }
            schoolSettings={schoolSettings}
            onUpdateSettings={handleUpdateSettings}
            assessmentGrades={assessmentGrades}
            attendanceRecords={attendanceRecords}
            onUpdateTeacher={(updated) => {
              const newTeachers = teachers.map((t) =>
                t.id === updated.id ? updated : t,
              );
              setTeachers(newTeachers);
              saveToApi("teachers", updated, "PUT", updated.id);
              if (activeUser && activeUser.id === updated.id) {
                setActiveUser(updated);
                localStorage.setItem(
                  "smpn1_activeUser",
                  JSON.stringify(updated),
                );
              }
            }}
            isDarkMode={isDarkMode}
            appTheme={appTheme}
            onNextTheme={handleNextTheme}
            onSelectTheme={handleSelectTheme}
          />
        );
      case View.ManageTeachers:
        return (
          <TeachersManagementView
            teachers={teachers}
            onUpdate={handleUpdateTeachers}
            availableClasses={classes}
          />
        );
      case View.ManageClasses:
        const handleSyncClassesToSettings = (newClasses: string[]) => {
          setClasses(newClasses);
          localStorage.setItem("smpn1_classes", JSON.stringify(newClasses));
          if (schoolSettings) {
            handleUpdateSettings({ ...schoolSettings, classes: newClasses });
          }
        };

        const handleAddClass = (className: string) => {
          const formatted = className.trim().toUpperCase();
          if (!formatted || classes.includes(formatted)) return;
          const updatedClasses = [...classes, formatted].sort();
          handleSyncClassesToSettings(updatedClasses);
        };

        const handleRenameClass = (oldName: string, newName: string) => {
          const formattedNew = newName.trim().toUpperCase();
          if (!formattedNew || oldName === formattedNew) return;

          const newClasses = classes.map((c) => (c === oldName ? formattedNew : c)).sort();
          handleSyncClassesToSettings(newClasses);

          const updatedTeachers = teachers.map((t) => {
            if (t.assignedClasses && t.assignedClasses.includes(oldName)) {
              const newAssigned = t.assignedClasses.map((c) => (c === oldName ? formattedNew : c));
              const updated = { ...t, assignedClasses: newAssigned };
              saveToApi("teachers", updated, "PUT", t.id);
              return updated;
            }
            return t;
          });
          setTeachers(updatedTeachers);

          const updatedStudents = students.map((s) => {
            if (s.class === oldName) {
              const updated = { ...s, class: formattedNew };
              saveToApi("students", updated, "PUT", s.id);
              return updated;
            }
            return s;
          });
          setStudents(updatedStudents);

          setSchedules((prev) =>
            prev.map((s) => {
              if (s.class === oldName) {
                const updated = { ...s, class: formattedNew };
                saveToApi("schedules", updated, "PUT", s.id);
                return updated;
              }
              return s;
            })
          );

          setAgendas((prev) =>
            prev.map((a) => {
              if (a.class === oldName) {
                const updated = { ...a, class: formattedNew };
                saveToApi("agendas", updated, "PUT", a.id);
                return updated;
              }
              return a;
            })
          );

          setAttendanceRecords((prev) =>
            prev.map((att) => {
              if (att.class === oldName) {
                const updated = { ...att, class: formattedNew };
                saveToApi("attendance", updated, "PUT", att.id);
                return updated;
              }
              return att;
            })
          );

          setAssessmentGrades((prev) =>
            prev.map((g) => {
              if (g.class === oldName) {
                const updated = { ...g, class: formattedNew };
                saveToApi("assessment-grades", updated, "PUT", g.id);
                return updated;
              }
              return g;
            })
          );

          setSasGrades((prev) =>
            prev.map((g) => {
              if (g.class === oldName) {
                const updated = { ...g, class: formattedNew };
                saveToApi("sas-grades", updated, "PUT", g.id);
                return updated;
              }
              return g;
            })
          );

          setFormativeGradesTP((prev) =>
            prev.map((g) => {
              if (g.class === oldName) {
                const updated = { ...g, class: formattedNew };
                saveToApi("formative-grades", updated, "PUT", g.id);
                return updated;
              }
              return g;
            })
          );

          setTugasList((prev) =>
            prev.map((t) => {
              let changed = false;
              let newT = { ...t };
              if (t.class === oldName) {
                newT.class = formattedNew;
                changed = true;
              }
              if (t.classes && t.classes.includes(oldName)) {
                newT.classes = t.classes.map((c) => (c === oldName ? formattedNew : c));
                changed = true;
              }
              if (changed) {
                saveToApi("tugas", newT, "PUT", t.id);
                return newT;
              }
              return t;
            })
          );
        };

        const handleDeleteClass = (clsName: string) => {
          const updatedClasses = classes.filter((c) => c !== clsName);
          handleSyncClassesToSettings(updatedClasses);

          const updatedTeachers = teachers.map((t) => ({
            ...t,
            assignedClasses: (t.assignedClasses || []).filter((c) => c !== clsName),
          }));
          setTeachers(updatedTeachers);
          updatedTeachers
            .filter(
              (t) =>
                t.assignedClasses.length !==
                teachers.find((o) => o.id === t.id)?.assignedClasses?.length,
            )
            .forEach((t) => saveToApi("teachers", t, "PUT", t.id));

          const updatedStudents = students.map((s) =>
            s.class === clsName ? { ...s, class: "" } : s,
          );
          setStudents(updatedStudents);
          updatedStudents
            .filter(
              (s) => s.class !== students.find((o) => o.id === s.id)?.class,
            )
            .forEach((s) => saveToApi("students", s, "PUT", s.id));

          const deletedSchedules = schedules.filter(
            (s) => s.class === clsName,
          );
          setSchedules((prev) => prev.filter((s) => s.class !== clsName));
          deletedSchedules.forEach((s) =>
            saveToApi("schedules", null, "DELETE", s.id),
          );

          const affectedAgendas = agendas.filter(
            (a) => a.class === clsName,
          );
          setAgendas((prev) => prev.filter((a) => a.class !== clsName));
          affectedAgendas.forEach((a) =>
            saveToApi("agendas", null, "DELETE", a.id),
          );

          const affectedAgendasIds = affectedAgendas.map((a) => a.id);
          const affectedAttendance = attendanceRecords.filter((att) =>
            affectedAgendasIds.includes(att.agendaId),
          );
          setAttendanceRecords((prev) =>
            prev.filter(
              (att) => !affectedAgendasIds.includes(att.agendaId),
            ),
          );
          affectedAttendance.forEach((a) =>
            saveToApi("attendance", null, "DELETE", a.id),
          );

          const deletedAssessments = assessmentGrades.filter(
            (g) => g.class === clsName,
          );
          setAssessmentGrades((prev) =>
            prev.filter((g) => g.class !== clsName),
          );
          deletedAssessments.forEach((g) =>
            saveToApi("assessment-grades", null, "DELETE", g.id),
          );

          const deletedFormative = formativeGradesTP.filter(
            (g) => g.class === clsName,
          );
          setFormativeGradesTP((prev) =>
            prev.filter((g) => g.class !== clsName),
          );
          deletedFormative.forEach((g) =>
            saveToApi("formative-grades", null, "DELETE", g.id),
          );
        };

        const handleClearAllClasses = () => {
          handleSyncClassesToSettings([]);
          const updatedTeachers = teachers.map((t) => ({
            ...t,
            assignedClasses: [],
          }));
          setTeachers(updatedTeachers);
          updatedTeachers.forEach((t) => saveToApi("teachers", t, "PUT", t.id));
        };

        return (
          <ClassesManagementView
            classes={classes}
            setClasses={handleSyncClassesToSettings}
            onAddClass={handleAddClass}
            onRenameClass={handleRenameClass}
            onDeleteClass={handleDeleteClass}
            onClearAllClasses={handleClearAllClasses}
            teachers={teachers}
            students={students}
            onUpdateStudents={handleUpdateStudents}
          />
        );
      case View.ManageStudents:
        return (
          <StudentsView
            students={students}
            onUpdate={handleUpdateStudents}
            availableClasses={classes}
            schoolSettings={schoolSettings}
            teachers={teachers}
            onUpdateTeachers={handleUpdateTeachers}
            onUpdateTeacher={handleUpdateTeacher}
            userRole={role}
            activeUser={activeUser as Teacher}
            onStudentClick={(student) => {
              setSelectedStudentForProfile(student);
              setActiveView(View.StudentProfile);
            }}
          />
        );
      case View.AdminAgenda:
        return (
          <AdminAgendaView
            agendas={agendas}
            teachers={teachers}
            classes={classes}
            schoolSettings={schoolSettings}
            onNavigate={navigateTo}
            onDeleteAgenda={(agendaId) => {
              saveToApi("agendas", null, "DELETE", agendaId);
              setAgendas((prev) => prev.filter((a) => a.id !== agendaId));
            }}
            onUpdateAgenda={handleUpdateAgenda}
            appTheme={appTheme}
          />
        );
      case View.StudentList:
        return (
          <StudentsView
            students={(students || []).filter((s) =>
              activeUser?.assignedClasses?.includes(s.class),
            )}
            onUpdate={handleUpdateStudents}
            availableClasses={activeUser?.assignedClasses || []}
            schoolSettings={schoolSettings}
            teachers={teachers}
            onUpdateTeachers={handleUpdateTeachers}
            onUpdateTeacher={handleUpdateTeacher}
            userRole={role}
            activeUser={activeUser as Teacher}
            onStudentClick={(student) => {
              setSelectedStudentForProfile(student);
              setActiveView(View.StudentProfile);
            }}
          />
        );
      case View.Perwalian:
      case View.WaliRombel:
        return (
          <PerwalianView
            waliRombel={waliRombel}
            students={students}
            classes={classes}
            activeUser={activeUser as Teacher}
            schoolSettings={schoolSettings}
            onSaveWali={handleSaveWali}
            onUpdateTeacher={handleUpdateTeacher}
            onUpdateStudents={handleUpdateStudents}
            attendanceRecords={homeroomAttendance}
            onUpdateAttendance={handleUpdateHomeroomAttendance}
            userRole={role}
            homeroomNotes={homeroomNotes}
            onSaveHomeroomNote={handleSaveHomeroomNote}
            academicProgress={studentAcademicProgress}
            onSaveAcademicProgress={handleSaveAcademicProgress}
            extracurriculars={extracurriculars}
            onSaveExtracurricular={handleSaveExtracurricular}
            onUpdateSettings={handleUpdateSettings}
            appTheme={appTheme}
            agendas={agendas}
            agendaAttendanceRecords={attendanceRecords}
            schedules={schedules}
          />
        );
      case View.Settings:
        return (
          <SettingsView
            role={role}
            settings={schoolSettings}
            onUpdate={async (newSettings) => {
              return await handleUpdateSettings(newSettings);
            }}
          />
        );
      case View.PeriodSettings:
        return (
          <PeriodSettingsView
            settings={schoolSettings}
            onUpdate={async (newSettings) => {
              return await handleUpdateSettings(newSettings);
            }}
          />
        );
      case View.Database:
        return <DatabaseManager />;
      case View.Schedule:
        return (
          <ScheduleView
            schedules={schedules}
            setSchedules={handleUpdateSchedules}
            availableClasses={activeUser?.assignedClasses || []}
            teacherSubject={activeUser?.subject || "Umum"}
            schoolSettings={schoolSettings}
            userRole={role}
            onUpdateSettings={handleUpdateSettings}
            activeUser={activeUser}
            students={students}
            agendas={agendas}
            attendanceRecords={attendanceRecords}
          />
        );
      case View.LearningObjectives:
        return (
          <LearningObjectivesView
            objectives={learningObjectives}
            onUpdate={handleUpdateObjectives}
            teacherSubject={activeUser?.subject || "Umum"}
            activeUser={activeUser}
            schoolSettings={schoolSettings}
            userRole={role}
          />
        );
      case View.Agenda:
        return (
          <AgendaView
            agendas={agendas}
            setAgendas={handleUpdateAgendas}
            schedules={schedules}
            availableClasses={activeUser?.assignedClasses || []}
            teacherSubject={activeUser?.subject || ""}
            objectives={(learningObjectives || []).filter(
              (obj) =>
                obj.subject?.trim().toLowerCase() ===
                activeUser?.subject?.trim().toLowerCase(),
            )}
            activeUser={activeUser}
            onNavigate={navigateTo}
            students={students}
            attendanceRecords={attendanceRecords}
            onUpdateAttendance={handleUpdateAttendance}
            schoolSettings={schoolSettings}
            initialAgenda={targetGradingAgenda}
            initialTab={targetAgendaTab}
            appTheme={appTheme}
          />
        );
      case View.Attendance:
        return (
          <AttendanceView
            students={students}
            classes={activeUser?.assignedClasses || []}
            schedules={schedules}
            role={role}
            agendas={agendas}
            attendanceRecords={attendanceRecords}
            onUpdateAttendance={handleUpdateAttendance}
            activeUser={activeUser}
            schoolSettings={schoolSettings}
            initialTab={targetAttendanceTab}
            onNavigate={navigateTo}
            appTheme={appTheme}
          />
        );
      case View.Grading:
        return (
          <GradingView
            students={students}
            agendas={agendas}
            objectives={(learningObjectives || []).filter(
              (obj) =>
                obj.subject?.trim().toLowerCase() ===
                activeUser?.subject?.trim().toLowerCase(),
            )}
            assessmentGrades={assessmentGrades}
            sasGrades={sasGrades}
            formativeGradesTP={formativeGradesTP}
            tugas={tugasList}
            tugasSubmissions={tugasSubmissions}
            onUpdateGrades={handleUpdateGrades}
            onUpdateSasGrades={handleUpdateSasGrades}
            onUpdateFormativeTP={handleUpdateFormativeTP}
            onUpdateObjectives={(objs) => setLearningObjectives(objs)}
            onUpdateTeacher={handleUpdateTeacher}
            activeUser={activeUser}
            initialAgenda={targetGradingAgenda}
            schoolSettings={schoolSettings}
            attendanceRecords={attendanceRecords}
            selectedStudentId={selectedStudentForGrading?.id}
            role={role}
            onStudentRecapClick={(student) => {
              setSelectedStudentForGrading(student);
              setActiveView(View.NilaiRapor);
            }}
            appTheme={appTheme}
          />
        );
      case View.NilaiRapor:
        return (
          <NilaiRaporView
            students={students}
            agendas={agendas}
            attendanceRecords={attendanceRecords}
            assessmentGrades={assessmentGrades}
            sasGrades={sasGrades}
            activeUser={activeUser}
            objectives={(learningObjectives || []).filter(
              (obj) =>
                obj.subject?.trim().toLowerCase() ===
                activeUser?.subject?.trim().toLowerCase(),
            )}
            formativeGradesTP={formativeGradesTP}
            schoolSettings={schoolSettings}
            onUpdateTeacher={handleUpdateTeacher}
            onStudentClick={(student) => {
              setSelectedStudentForProfile(student);
              setActiveView(View.StudentProfile);
            }}
            onStudentRecapClick={(student) => {
              setSelectedStudentForGrading(student);
              setActiveView(View.Grading);
            }}
            onUpdateSasGrades={handleUpdateSasGrades}
            onUpdateAssessmentGrades={handleUpdateGrades}
            onUpdateFormativeTP={handleUpdateFormativeTP}
            selectedStudentId={selectedStudentForGrading?.id}
            appTheme={appTheme}
          />
        );
      case View.MaterialGenerator:
        return (
          <MaterialGeneratorView
            activeUser={activeUser}
            objectives={(learningObjectives || []).filter(
              (o) =>
                o.subject?.trim().toLowerCase() ===
                activeUser?.subject?.trim().toLowerCase(),
            )}
            onSaveTugas={handleSaveTugas}
            onNavigateToTugas={() => setActiveView(View.Tugas)}
          />
        );
      case View.Tugas:
        return (
          <TugasView
            role={role}
            activeUser={activeUser}
            tugas={tugasList}
            tugasSubmissions={tugasSubmissions}
            tugasSessions={tugasSessions}
            onStartSession={handleStartTugasSession}
            onEndSession={handleEndTugasSession}
            students={students}
            classes={
              role === UserRole.Guru ? activeUser?.assignedClasses || [] : []
            }
            objectives={(learningObjectives || []).filter((obj) => {
              const subjectFilter =
                role === UserRole.Guru
                  ? activeUser?.subject
                  : selectedSubjectForStudent;

              if (!subjectFilter) return false;
              return (
                obj.subject?.trim().toLowerCase() ===
                subjectFilter?.trim().toLowerCase()
              );
            })}
            assessmentGrades={assessmentGrades}
            formativeGradesTP={formativeGradesTP}
            onUpdateGrades={handleUpdateGrades}
            onSaveTugas={handleSaveTugas}
            onDeleteTugas={handleDeleteTugas}
            onDeleteSubmission={handleDeleteSubmission}
            onUpdateSubmission={handleUpdateSubmission}
            onSubmitTugas={handleSubmitTugas}
            onRefreshSubmissions={refreshTugasSubmissions}
            selectedSubjectForStudent={selectedSubjectForStudent}
            filterType={"all"}
            onStudentClick={(student) => {
              setSelectedStudentForProfile(student);
              setActiveView(View.StudentProfile);
            }}
            schoolSettings={schoolSettings}
            initialTugasId={targetTugasId}
            courseModules={courseModules}
            courseSubmissions={courseSubmissions}
            onSaveCourseModule={handleSaveCourseModule}
            onDeleteCourseModule={handleDeleteCourseModule}
            onSaveCourseSubmission={handleSaveCourseSubmission}
            onSaveAssessmentGrade={handleSaveAssessmentGrade}
          />
        );
      case View.StudentAgenda:
        return (
          <StudentAgendaView
            student={activeUser as Student}
            agendas={agendas}
            objectives={learningObjectives}
            onNavigate={navigateTo}
            onFetchOnDemand={handleFetchOnDemand}
            appTheme={appTheme}
          />
        );
      case View.Profile:
        return (
          <ProfileView
            user={activeUser}
            availableClasses={classes}
            onUpdateUser={handleUpdateTeacher}
          />
        );
      case View.StudentGrade:
      case View.StudentProfile:
        return (
          <StudentProfileView
            student={
              selectedStudentForProfile ||
              (role === UserRole.Siswa ? activeUser : null)
            }
            students={students}
            agendas={agendas}
            attendanceRecords={attendanceRecords}
            assessmentGrades={assessmentGrades}
            sasGrades={sasGrades}
            formativeGradesTP={formativeGradesTP}
            objectives={learningObjectives}
            schedules={schedules}
            teachers={teachers}
            teacherSubject={activeUser?.subject || ""}
            onBack={() => navigateTo(View.Dashboard)}
            tugas={tugasList}
            tugasSubmissions={tugasSubmissions}
            onSaveTugas={handleSaveTugas}
            onDeleteTugas={handleDeleteTugas}
            onSubmitTugas={handleSubmitTugas}
            role={role}
            schoolSettings={schoolSettings}
            onFetchOnDemand={handleFetchOnDemand}
            initialTab={activeView === View.StudentGrade ? 'nilai' : 'profil'}
            onTabChange={(tab) => {
              setActiveView(tab === 'nilai' ? View.StudentGrade : View.StudentProfile);
            }}
          />
        );
      case View.SubjectSelection:
        return (
          <SubjectSelectionView
            student={activeUser as Student}
            schedules={schedules}
            agendas={agendas}
            teachers={teachers}
            onComplete={(subject) => {
              setSelectedSubjectForStudent(subject);
              logProfileAccess(activeUser!.id, (activeUser as Student).class);
              setActiveView(View.StudentProfile);
            }}
          />
        );
      case View.ProfileAccessHistory:
        return (
          <ProfileAccessHistoryView
            logs={profileAccessLogs}
            students={students}
            onDelete={deleteProfileAccessLog}
            onClearAll={clearAllProfileAccessLogs}
            assessmentGrades={assessmentGrades}
            sasGrades={sasGrades}
            formativeGradesTP={formativeGradesTP}
            objectives={learningObjectives}
            activeUser={activeUser}
          />
        );
      default:
        return (
          <DashboardView
            role={role}
            activeUser={activeUser}
            schedules={schedules}
            agendas={agendas}
            students={students}
            teachers={teachers}
            onNavigate={navigateTo}
            onUpdateAgenda={handleUpdateAgenda}
            schoolSettings={schoolSettings}
            onUpdateSettings={handleUpdateSettings}
            assessmentGrades={assessmentGrades}
            attendanceRecords={attendanceRecords}
            onUpdateTeacher={handleUpdateTeacher}
            isDarkMode={isDarkMode}
          />
        );
    }
  };

  return (
    <AnimatePresence mode="wait">
      {isLoading ||
      quotaError ||
      (schoolSettings.isStudentMaintenanceMode && role === UserRole.Siswa) ? (
        quotaError ||
        (schoolSettings.isStudentMaintenanceMode && role === UserRole.Siswa) ? (
          <motion.div
            key="maintenance"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <MaintenanceView
              message={
                quotaError ||
                "Halaman siswa sedang dalam mode maintenance oleh admin sekolah. Silakan kembali lagi nanti."
              }
              onRetry={handleManualRetry}
            />
          </motion.div>
        ) : (
          <motion.div
            key="loader"
            exit={{ opacity: 0, scale: 1.05 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className={`min-h-screen flex items-center justify-center ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}
          >
            <div className="w-full max-w-sm px-8 text-center">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="mb-8 relative inline-block"
              >
                <div className="w-24 h-24 bg-indigo-600 rounded-3xl flex items-center justify-center text-white shadow-2xl shadow-indigo-500/40 relative z-10">
                  <GraduationCap size={48} />
                </div>
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.2, 0.4, 0.2],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute inset-0 bg-indigo-500 rounded-3xl blur-2xl -z-10"
                />
              </motion.div>

              <motion.h1
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className={`text-2xl font-black tracking-tight mb-2 ${isDarkMode ? "text-white" : "text-slate-900"}`}
              >
                SMPN 1 KAWALI
              </motion.h1>

              <motion.p
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-slate-500 font-medium text-sm mb-8"
              >
                Sistem Administrasi Digital Terpadu
              </motion.p>

              <div className="relative h-2 w-full bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden mb-4">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${loadingProgress}%` }}
                  className="absolute top-0 left-0 h-full bg-indigo-600 rounded-full shadow-[0_0_10px_rgba(79,70,229,0.5)]"
                />
              </div>

              <div className="flex items-center justify-center gap-2 text-indigo-600 dark:text-indigo-400 font-black text-[10px] uppercase tracking-widest mb-6">
                <Loader2 size={12} className="animate-spin" />
                <span>{loadingStatus}</span>
              </div>

              {loadingProgress > 0 && loadingProgress < 100 && (
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 5 }}
                  onClick={() => {
                    setIsLoading(false);
                    setLoadingStatus("Siap");
                  }}
                  className="text-[10px] bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 font-bold px-4 py-2 rounded-xl transition-all"
                >
                  Lewati Sinkronisasi (Gunakan Cache)
                </motion.button>
              )}
            </div>
          </motion.div>
        )
      ) : !isAuthenticated ? (
        <motion.div
          key="login"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Login onLoginSubmit={handleLoginSubmit} />
        </motion.div>
      ) : (
        <motion.div
          key="app"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="flex h-screen overflow-hidden app-canvas text-slate-900 dark:text-slate-100 transition-colors duration-300"
        >
          <div className="hidden lg:block no-print h-full">
            <Sidebar
              role={role}
              activeView={activeView}
              setActiveView={setActiveView}
              isSidebarOpen={isSidebarOpen}
              setSidebarOpen={setSidebarOpen}
              isDarkMode={isDarkMode}
              schoolSettings={schoolSettings}
              appTheme={appTheme}
            />
          </div>
          <div className="lg:hidden no-print">
            <BottomNav
              role={role}
              activeView={activeView}
              setActiveView={setActiveView}
              isMaintenance={schoolSettings.isStudentMaintenanceMode}
              appTheme={appTheme}
            />
          </div>
          <div className="flex-1 flex flex-col overflow-hidden">
            {quotaError && role !== UserRole.Siswa && (
              <div className="bg-rose-500 text-white px-4 py-3 flex items-start sm:items-center justify-between no-print z-50 shadow-md">
                <div className="flex items-center gap-3 text-sm font-medium leading-relaxed">
                  <AlertTriangle size={20} className="shrink-0" />
                  <span>{quotaError}</span>
                </div>
                <button
                  onClick={handleManualRetry}
                  className="p-1.5 hover:bg-rose-600 rounded-lg transition-colors shrink-0 ml-4"
                  title="Retry"
                >
                  <X size={16} />
                </button>
              </div>
            )}
            {(!role || role !== UserRole.Siswa || !quotaError) && (
              <div className="no-print">
                <Header
                  role={role}
                  userName={activeUser?.name || "User"}
                  sidebarOpen={isSidebarOpen}
                  setSidebarOpen={setSidebarOpen}
                  activeView={activeView}
                  isDarkMode={isDarkMode}
                  themeMode={themeMode}
                  setThemeMode={setThemeMode}
                  toggleTheme={toggleTheme}
                  onLogout={handleLogout}
                  onOpenProfile={() => setActiveView(View.Profile)}
                  activeUser={activeUser}
                  agendas={agendas}
                  schedules={schedules}
                  students={students}
                  attendanceRecords={attendanceRecords}
                  tugasList={tugasList}
                  tugasSubmissions={tugasSubmissions}
                  notificationsProp={notifications}
                  onNavigate={navigateTo}
                  isBackgroundLoading={isBackgroundLoading}
                  onUpdateUser={handleUpdateTeacher}
                  onDeleteNotificationData={handleDeleteNotificationData}
                  appTheme={appTheme}
                  onNextTheme={handleNextTheme}
                  onSelectTheme={handleSelectTheme}
                  schoolSettings={schoolSettings}
                />
              </div>
            )}
            <main
              ref={mainContentRef}
              className={`flex-1 overflow-x-hidden overflow-y-auto p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8 min-h-0`}
            >
              <div className="max-w-7xl mx-auto">
                {renderView()}
              </div>
            </main>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default App;
